import * as THREE from 'three';
import { isKitNumber, KIT_NUMBER_DEFAULT,
  TIFO_STYLE_DEFAULT,
  isTifoStyle,
  cosmeticById,
  type FlagSpec,
  type FlagEmblem,
  type KitSpec,
  type WheelArtKind,
} from '@/config/cosmetics';
import type { VehicleLoadout } from '@/core/SaveData';
import { buildLiveryAtlas, buildLiverySide } from '@/entities/livery/atlas';
import { debugPainter, kitLivery } from '@/entities/livery/kits';
import { DEFAULT_BODY, flankCover, foldCover, offLayerShare, planCover, smearCover, type BodyFit, type FlankMap } from '@/entities/livery/types';

/**
 * carLook.ts — resolves a saved cosmetic loadout (item ids) into the concrete
 * render inputs `applyCarMaterials` consumes (M79), plus the stylized-flag
 * canvas painter the wraps and the shop thumbnails share. COSMETIC ONLY —
 * nothing here can touch physics.
 *
 * The equipped PLAYER look is also published here (set by Flow whenever the
 * save changes) so Race can dress the player's car without threading save
 * state through Game — display-layer state, never sim state. ONLINE (M81):
 * every racer's look syncs through the room (join URL / lobby messages →
 * roster + welcome) and NetRace dresses the whole field from it.
 */

export interface CarLook {
  body?: number;
  trim?: number;
  rim?: number;
  rimChrome?: boolean;
  tyre?: number;
  /** M114: the tyre RUBBER renders as polished metal (Golden Boot gold /
   *  Quicksilver silver) — CarView swaps the matte-rubber material values
   *  for high metalness + low roughness. Display only. */
  tyreMetal?: boolean;
  /** M144: the wheels wear a PAINTED face (tyre + rim together) instead of
   *  flat colours — entities/livery/wheels. Display only. */
  wheelArt?: WheelArtKind;
  /** The body texture painted for the DEFAULT_FIT body (the procedural
   *  placeholder + dozer form use it directly). A real GLB gets a texture
   *  painted for ITS body via `livery` + `liveryTexture` (M127). */
  wrapMap?: THREE.Texture;
  /** M127: what the body wears, so applyCarMaterials can paint it for the
   *  exact vehicle it is dressing (belt line + proportions differ per ride). */
  livery?: LiverySpec;
  /** M128: the equipped MODS (physical bolt-ons, built by entities/mods) and
   *  the picked kit number. Display only, like everything in this look. */
  mods?: { ids: string[]; kitNumber: number; tifoStyle: number };
}

/** M128 — the two colours a mod part paints with: the kit's first colour (or
 *  the flag's first band, or the body/stock paint) and its second. The wrap
 *  TEXTURE never lands on a bolt-on (it is projected for the body), so parts
 *  read as accents in the kit's colours instead. */
export function lookColors(look: CarLook | undefined, stockColor: number): { primary: number; secondary: number } {
  const kit = look?.livery?.kit;
  const flag = look?.livery?.flag;
  if (kit) return { primary: kit.colors[0] ?? stockColor, secondary: kit.colors[1] ?? 0xf4f6fa };
  if (flag) return { primary: flag.colors[0] ?? stockColor, secondary: flag.colors[1] ?? 0xf4f6fa };
  return { primary: look?.body ?? stockColor, secondary: 0xf4f6fa };
}

// ---------------------------------------------------------------------------
// Flag painter — FlagSpec → canvas. Deliberately stylized (bands + a small
// emblem vocabulary); shared by the wrap texture and the shop's card art.
// ---------------------------------------------------------------------------

const css = (n: number): string => `#${n.toString(16).padStart(6, '0')}`;

function starPath(g: CanvasRenderingContext2D, cx: number, cy: number, r: number, points = 5): void {
  g.beginPath();
  const inner = points === 5 ? 0.42 : 0.5;
  for (let i = 0; i < points * 2; i++) {
    const rad = i % 2 === 0 ? r : r * inner;
    const a = -Math.PI / 2 + (i * Math.PI) / points;
    const x = cx + Math.cos(a) * rad;
    const y = cy + Math.sin(a) * rad;
    if (i === 0) g.moveTo(x, y);
    else g.lineTo(x, y);
  }
  g.closePath();
}

/** M153: the Union Jack (Australia's canton), painted into a box of any
 *  size. Simplified the way every flag here is: the white saltire with a
 *  thin red one centred on it, then the white and red crosses. */
function unionJack(g: CanvasRenderingContext2D, x0: number, y0: number, cw: number, ch: number): void {
  g.save();
  g.beginPath();
  g.rect(x0, y0, cw, ch);
  g.clip();
  g.fillStyle = '#012169';
  g.fillRect(x0, y0, cw, ch);
  const diag = (col: string, lw: number): void => {
    g.strokeStyle = col;
    g.lineWidth = lw;
    g.beginPath();
    g.moveTo(x0, y0);
    g.lineTo(x0 + cw, y0 + ch);
    g.moveTo(x0 + cw, y0);
    g.lineTo(x0, y0 + ch);
    g.stroke();
  };
  diag('#ffffff', ch * 0.2);
  diag('#c8102e', ch * 0.067);
  g.fillStyle = '#ffffff';
  g.fillRect(x0, y0 + ch / 2 - ch / 6, cw, ch / 3);
  g.fillRect(x0 + cw / 2 - ch / 6, y0, ch / 3, ch);
  g.fillStyle = '#c8102e';
  g.fillRect(x0, y0 + ch / 2 - ch / 10, cw, ch / 5);
  g.fillRect(x0 + cw / 2 - ch / 10, y0, ch / 5, ch);
  g.restore();
}

/** M153: the red dragon of Wales, passant, facing the hoist (left), in a box
 *  about 0.76 x 0.52 of `s` centred on (cx, cy). A heraldic silhouette in
 *  curves: long neck, open jaws, one raised scalloped wing, the near forepaw
 *  lifted, the tail curling up over the back to an arrow tip. */
function paintWyvern(g: CanvasRenderingContext2D, color: string, cx: number, cy: number, s: number): void {
  const k = 0.4 * s;
  const X = (x: number): number => cx + x * k;
  const Y = (y: number): number => cy + y * k;
  const M = (x: number, y: number): void => g.moveTo(X(x), Y(y));
  const L = (x: number, y: number): void => g.lineTo(X(x), Y(y));
  const Q = (qx: number, qy: number, x: number, y: number): void => g.quadraticCurveTo(X(qx), Y(qy), X(x), Y(y));
  const C = (ax: number, ay: number, bx: number, by: number, x: number, y: number): void =>
    g.bezierCurveTo(X(ax), Y(ay), X(bx), Y(by), X(x), Y(y));
  g.save();
  g.fillStyle = color;
  g.strokeStyle = color;
  g.lineJoin = 'round';
  g.lineCap = 'round';
  // the raised wing: a leading edge sweeping up and back to two finger tips,
  // the trailing edge scalloped down to the root on the back
  g.beginPath();
  M(-0.16, -0.08);
  C(-0.26, -0.3, -0.24, -0.58, -0.1, -0.84);
  Q(0.06, -0.7, 0.14, -0.78);
  Q(0.22, -0.62, 0.36, -0.66);
  Q(0.3, -0.52, 0.4, -0.46);
  Q(0.28, -0.4, 0.34, -0.3);
  Q(0.22, -0.26, 0.26, -0.16);
  Q(0.14, -0.14, 0.14, -0.06);
  g.closePath();
  g.fill();
  // body, neck and head in one outline
  g.beginPath();
  M(0.5, 0.0); // the haunch
  C(0.5, -0.12, 0.3, -0.14, 0.08, -0.12); // the back
  C(-0.1, -0.11, -0.3, -0.16, -0.4, -0.3); // shoulder up the back of the neck
  C(-0.46, -0.4, -0.5, -0.5, -0.56, -0.54); // the nape
  L(-0.5, -0.66); // a horn
  L(-0.62, -0.56);
  C(-0.7, -0.58, -0.82, -0.56, -0.9, -0.5); // brow to the snout
  L(-0.96, -0.47);
  L(-0.74, -0.42); // the upper jaw, open
  L(-0.92, -0.33); // the lower jaw
  L(-0.68, -0.34);
  C(-0.62, -0.24, -0.56, -0.1, -0.44, 0.02); // the throat down the front of the neck
  C(-0.38, 0.12, -0.24, 0.17, -0.04, 0.16); // chest to belly
  C(0.18, 0.16, 0.36, 0.16, 0.46, 0.12);
  Q(0.54, 0.08, 0.5, 0.0);
  g.closePath();
  g.fill();
  // the forked tongue
  g.lineWidth = 0.03 * k;
  g.beginPath();
  M(-0.82, -0.38);
  L(-1.02, -0.4);
  M(-0.96, -0.4);
  L(-1.03, -0.34);
  g.stroke();
  // legs: the near forepaw raised, the others planted, three claws each
  const leg = (pts: [number, number][], w: number): void => {
    g.lineWidth = w * k;
    g.beginPath();
    M(...pts[0]);
    for (const [x, y] of pts.slice(1)) L(x, y);
    g.stroke();
  };
  const claws = (x: number, y: number, dir: number): void => {
    g.lineWidth = 0.035 * k;
    g.beginPath();
    for (const d of [-0.06, 0, 0.06]) {
      M(x, y);
      L(x + dir * 0.09, y + d);
    }
    g.stroke();
  };
  leg([[-0.3, 0.06], [-0.44, 0.22], [-0.56, 0.16]], 0.1);
  claws(-0.56, 0.16, -1);
  leg([[-0.16, 0.12], [-0.18, 0.3], [-0.24, 0.43]], 0.1);
  claws(-0.24, 0.43, -1);
  leg([[0.26, 0.12], [0.2, 0.28], [0.26, 0.43]], 0.11);
  claws(0.26, 0.43, -1);
  leg([[0.42, 0.08], [0.5, 0.26], [0.46, 0.43]], 0.11);
  claws(0.46, 0.43, -1);
  // the tail: back and down from the haunch, then up over the back
  g.lineWidth = 0.075 * k;
  g.beginPath();
  M(0.48, 0.04);
  C(0.72, 0.16, 0.9, 0.06, 0.86, -0.14);
  C(0.82, -0.3, 0.66, -0.32, 0.6, -0.22);
  g.stroke();
  // the arrow tip
  g.beginPath();
  M(0.54, -0.16);
  L(0.63, -0.3);
  L(0.66, -0.15);
  Q(0.6, -0.2, 0.54, -0.16);
  g.closePath();
  g.fill();
  g.restore();
}

/** M153: a heater shield, 0.4 x 0.5 of `s`, centred on (cx, cy). */
function shieldPath(g: CanvasRenderingContext2D, cx: number, cy: number, s: number): void {
  const hw = 0.2 * s;
  const top = cy - 0.25 * s;
  const shoulder = cy + 0.04 * s;
  const tip = cy + 0.25 * s;
  g.beginPath();
  g.moveTo(cx - hw, top);
  g.lineTo(cx + hw, top);
  g.lineTo(cx + hw, shoulder);
  g.quadraticCurveTo(cx + hw, tip - 0.04 * s, cx, tip);
  g.quadraticCurveTo(cx - hw, tip - 0.04 * s, cx - hw, shoulder);
  g.closePath();
}

/** M153: the two coats of arms the tricolours need (simplified, no text). */
function paintShield(g: CanvasRenderingContext2D, arms: 'slovakia' | 'slovenia' | 'serbia', cx: number, cy: number, s: number): void {
  g.save();
  if (arms === 'serbia') {
    // the Serbian cross: a white cross on red with a firesteel in each
    // quarter, gold rim (the inner shield of the arms, without the eagle)
    shieldPath(g, cx, cy, s);
    g.fillStyle = '#c6363c';
    g.fill();
    g.save();
    g.clip();
    g.fillStyle = '#ffffff';
    const bw = 0.045 * s;
    g.fillRect(cx - bw / 2, cy - 0.25 * s, bw, 0.5 * s);
    g.fillRect(cx - 0.2 * s, cy - 0.04 * s - bw / 2, 0.4 * s, bw);
    g.strokeStyle = '#ffffff';
    g.lineWidth = 0.022 * s;
    for (const [dx, dy, flip] of [[-0.095, -0.14, 1], [0.095, -0.14, -1], [-0.095, 0.07, 1], [0.095, 0.07, -1]] as const) {
      g.beginPath();
      g.arc(cx + dx * s, cy + dy * s, 0.04 * s, flip > 0 ? Math.PI * 0.35 : Math.PI * 1.35, flip > 0 ? Math.PI * 1.65 : Math.PI * 2.65, false);
      g.stroke();
    }
    g.restore();
    shieldPath(g, cx, cy, s);
    g.strokeStyle = '#e8c547';
    g.lineWidth = 0.024 * s;
    g.stroke();
  } else if (arms === 'slovakia') {
    // red shield, white double cross on three blue hills, white rim
    shieldPath(g, cx, cy, s);
    g.fillStyle = '#ee1c25';
    g.fill();
    g.save();
    g.clip();
    g.fillStyle = '#0b4ea2';
    for (const [dx, r] of [[-0.12, 0.085], [0.12, 0.085], [0, 0.1]] as const) {
      g.beginPath();
      g.arc(cx + dx * s, cy + 0.2 * s + r * 0.35 * s, r * s, 0, Math.PI * 2);
      g.fill();
    }
    g.fillStyle = '#ffffff';
    const bw = 0.045 * s;
    g.fillRect(cx - bw / 2, cy - 0.18 * s, bw, 0.31 * s);
    g.fillRect(cx - 0.085 * s, cy - 0.12 * s, 0.17 * s, bw * 0.9);
    g.fillRect(cx - 0.12 * s, cy - 0.03 * s, 0.24 * s, bw);
    g.restore();
    shieldPath(g, cx, cy, s);
    g.strokeStyle = '#ffffff';
    g.lineWidth = 0.022 * s;
    g.stroke();
  } else {
    // blue shield, red rim: white Triglav over two wavy lines, three gold stars
    shieldPath(g, cx, cy, s);
    g.fillStyle = '#005da4';
    g.fill();
    g.save();
    g.clip();
    g.fillStyle = '#ffffff';
    g.beginPath();
    g.moveTo(cx - 0.2 * s, cy + 0.13 * s);
    g.lineTo(cx - 0.11 * s, cy + 0.02 * s);
    g.lineTo(cx - 0.06 * s, cy + 0.06 * s);
    g.lineTo(cx, cy - 0.06 * s);
    g.lineTo(cx + 0.06 * s, cy + 0.06 * s);
    g.lineTo(cx + 0.11 * s, cy + 0.02 * s);
    g.lineTo(cx + 0.2 * s, cy + 0.13 * s);
    g.lineTo(cx + 0.2 * s, cy + 0.3 * s);
    g.lineTo(cx - 0.2 * s, cy + 0.3 * s);
    g.closePath();
    g.fill();
    g.strokeStyle = '#005da4';
    g.lineWidth = 0.018 * s;
    for (const yy of [0.15, 0.2]) {
      g.beginPath();
      for (let i = 0; i <= 16; i++) {
        const x = cx - 0.2 * s + (i / 16) * 0.4 * s;
        const y = cy + yy * s + Math.sin(i * 1.6) * 0.012 * s;
        if (i === 0) g.moveTo(x, y);
        else g.lineTo(x, y);
      }
      g.stroke();
    }
    g.fillStyle = '#ffdd00';
    for (const [dx, dy] of [[-0.07, -0.17], [0.07, -0.17], [0, -0.11]] as const) {
      starPath(g, cx + dx * s, cy + dy * s, 0.035 * s, 6);
      g.fill();
    }
    g.restore();
    shieldPath(g, cx, cy, s);
    g.strokeStyle = '#ed1c24';
    g.lineWidth = 0.026 * s;
    g.stroke();
  }
  g.restore();
}

function drawEmblem(g: CanvasRenderingContext2D, e: FlagEmblem, w: number, hgt: number): void {
  const cx = w / 2;
  const cy = hgt / 2;
  switch (e.kind) {
    case 'disc': {
      const r = (e.r ?? 0.12) * w;
      g.fillStyle = css(e.color);
      g.beginPath();
      g.arc((e.x ?? 0.5) * w, cy, r, 0, Math.PI * 2);
      g.fill();
      if (e.ring !== undefined) {
        // M153: a coat of arms read as a flat brown disc (a "mud stain" on
        // the door, round 2): Mexico's wreath, Ecuador's frame.
        g.strokeStyle = css(e.ring);
        g.lineWidth = 0.22 * r;
        g.beginPath();
        g.arc((e.x ?? 0.5) * w, cy, 0.89 * r, 0, Math.PI * 2);
        g.stroke();
      }
      break;
    }
    case 'star': {
      g.fillStyle = css(e.color);
      const n = e.n ?? 1;
      const size = n > 1 ? 0.055 * w : 0.11 * w;
      const y = (e.y ?? 0.5) * hgt;
      for (let i = 0; i < n; i++) {
        const x = n > 1 ? w * (0.5 + (i - (n - 1) / 2) * 0.14) : (e.x ?? 0.5) * w;
        starPath(g, x, y, size, e.points ?? 5);
        g.fill();
      }
      break;
    }
    case 'crescent': {
      if (e.disc !== undefined) {
        g.fillStyle = css(e.disc);
        g.beginPath();
        g.arc(cx, cy, 0.2 * w, 0, Math.PI * 2);
        g.fill();
      }
      // Outer circle clockwise + offset inner circle COUNTER-clockwise, filled
      // evenodd = a crescent without transparent compositing (maps have no alpha).
      // M153: clipped to the outer circle. Unclipped, the inner circle's
      // overhang past the outer one was ALSO filled (evenodd), closing the
      // horns into a ring (Türkiye, the review sheets).
      g.save();
      g.beginPath();
      g.arc(cx - 0.02 * w, cy, 0.13 * w, 0, Math.PI * 2);
      g.clip();
      g.fillStyle = css(e.color);
      g.beginPath();
      g.arc(cx - 0.02 * w, cy, 0.13 * w, 0, Math.PI * 2);
      g.arc(cx + 0.025 * w, cy, 0.105 * w, 0, Math.PI * 2, true);
      g.fill('evenodd');
      g.restore();
      g.fillStyle = css(e.color);
      starPath(g, cx + 0.075 * w, cy, 0.045 * w);
      g.fill();
      break;
    }
    case 'cross': {
      g.fillStyle = css(e.color);
      if (e.plump) {
        // M153: the Swiss proportions: each arm reaches 7/6 of its width past
        // the centre square (the old 0.09/0.16 read as a stubby plus).
        const t = 0.055 * w;
        const ext = 0.18 * w;
        g.fillRect(cx - t, cy - ext, t * 2, ext * 2);
        g.fillRect(cx - ext, cy - t, ext * 2, t * 2);
      } else {
        const t = 0.07 * hgt;
        g.fillRect(0, cy - t, w, t * 2);
        g.fillRect(cx - t, 0, t * 2, hgt);
      }
      break;
    }
    case 'nordic': {
      const x = 0.38 * w;
      if (e.outline !== undefined) {
        g.fillStyle = css(e.outline);
        g.fillRect(0, cy - 0.105 * hgt, w, 0.21 * hgt);
        g.fillRect(x - 0.075 * w, 0, 0.15 * w, hgt);
      }
      g.fillStyle = css(e.color);
      g.fillRect(0, cy - 0.065 * hgt, w, 0.13 * hgt);
      g.fillRect(x - 0.045 * w, 0, 0.09 * w, hgt);
      break;
    }
    case 'saltire': {
      g.strokeStyle = css(e.color);
      g.lineWidth = 0.14 * hgt;
      g.beginPath();
      g.moveTo(0, 0);
      g.lineTo(w, hgt);
      g.moveTo(w, 0);
      g.lineTo(0, hgt);
      g.stroke();
      break;
    }
    case 'pall': {
      // the Y: its arms leave the hoist corners and meet a third of the way
      // along, the stem runs level to the fly (stretched along the car)
      const px = 0.36 * w;
      const yPath = (): void => {
        g.beginPath();
        g.moveTo(-0.02 * w, -0.04 * hgt);
        g.lineTo(px, cy);
        g.lineTo(w * 1.02, cy);
        g.moveTo(-0.02 * w, hgt * 1.04);
        g.lineTo(px, cy);
      };
      g.lineJoin = 'miter';
      g.strokeStyle = css(e.white);
      g.lineWidth = 0.34 * hgt;
      yPath();
      g.stroke();
      g.strokeStyle = css(e.green);
      g.lineWidth = 0.2 * hgt;
      yPath();
      g.stroke();
      g.fillStyle = css(e.gold);
      g.beginPath();
      g.moveTo(0, 0.17 * hgt);
      g.lineTo(0.255 * w, cy);
      g.lineTo(0, 0.83 * hgt);
      g.closePath();
      g.fill();
      g.fillStyle = css(e.black);
      g.beginPath();
      g.moveTo(0, 0.25 * hgt);
      g.lineTo(0.2 * w, cy);
      g.lineTo(0, 0.75 * hgt);
      g.closePath();
      g.fill();
      break;
    }
    case 'wyvern': {
      paintWyvern(g, css(e.color), cx, cy, w);
      break;
    }
    case 'diag': {
      g.strokeStyle = css(e.color);
      g.lineWidth = 0.2 * hgt;
      g.beginPath();
      g.moveTo(0, hgt);
      g.lineTo(w, 0);
      g.stroke();
      break;
    }
    case 'canton': {
      g.fillStyle = css(e.color);
      if (e.cross !== undefined) {
        // M153 (Greece): the canton is five stripes deep and square; the
        // cross arm IS the third stripe, the upright the same width.
        const ch = (5 / 9) * hgt;
        g.fillRect(0, 0, ch, ch);
        g.fillStyle = css(e.cross);
        g.fillRect(0, (2 / 9) * hgt, ch, hgt / 9);
        g.fillRect(ch / 2 - hgt / 18, 0, hgt / 9, ch);
        break;
      }
      if (e.union) {
        unionJack(g, 0, 0, 0.5 * w, 0.5 * hgt);
        break;
      }
      const cwd = 0.45 * w;
      const chd = 0.5 * hgt;
      g.fillRect(0, 0, cwd, chd);
      const n = e.stars ?? 0;
      if (n > 0) {
        g.fillStyle = css(e.starColor ?? 0xffffff);
        if (n === 1) {
          starPath(g, cwd / 2, chd / 2, 0.2 * Math.min(cwd, chd));
          g.fill();
        } else {
          // M153: a grid that FITS the canton (4 rows of 3 ran off its foot).
          const rows = Math.max(1, Math.round(Math.sqrt((n * chd) / cwd)));
          const cols = Math.ceil(n / rows);
          const r = 0.32 * Math.min(cwd / cols, chd / rows);
          for (let i = 0; i < n; i++) {
            starPath(g, ((i % cols) + 0.5) * (cwd / cols), (Math.floor(i / cols) + 0.5) * (chd / rows), r);
            g.fill();
          }
        }
      }
      break;
    }
    case 'triangle': {
      g.fillStyle = css(e.color);
      g.beginPath();
      g.moveTo(0, 0);
      g.lineTo(0.45 * w, cy);
      g.lineTo(0, hgt);
      g.closePath();
      g.fill();
      break;
    }
    case 'diamond': {
      g.fillStyle = css(e.color);
      g.beginPath();
      g.moveTo(cx, 0.1 * hgt);
      g.lineTo(0.88 * w, cy);
      g.lineTo(cx, 0.9 * hgt);
      g.lineTo(0.12 * w, cy);
      g.closePath();
      g.fill();
      if (e.inner !== undefined) {
        g.fillStyle = css(e.inner);
        g.beginPath();
        g.arc(cx, cy, 0.16 * w, 0, Math.PI * 2);
        g.fill();
      }
      break;
    }
    case 'checker': {
      const cell = 0.05 * w;
      const cols = 5;
      const rows = 4;
      const x0 = cx - (cols * cell) / 2;
      const y0 = 0.18 * hgt;
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          g.fillStyle = css(e.colors[(r + c) % 2]);
          g.fillRect(x0 + c * cell, y0 + r * cell, cell, cell);
        }
      }
      if (e.edge !== undefined) {
        // M153: a thin shield edge, so the board reads as a crest and its
        // squares do not melt into the band around it (Croatia).
        g.strokeStyle = css(e.edge);
        g.lineWidth = 0.012 * w;
        g.strokeRect(x0, y0, cols * cell, rows * cell);
      }
      break;
    }
    case 'leaf': {
      // A stylized (very stylized) maple leaf: three lobes + a stem.
      g.fillStyle = css(e.color);
      const s = 0.16 * w;
      g.beginPath();
      g.moveTo(cx, cy - s);
      g.lineTo(cx + s * 0.55, cy - s * 0.1);
      g.lineTo(cx + s, cy - s * 0.35);
      g.lineTo(cx + s * 0.7, cy + s * 0.45);
      g.lineTo(cx, cy + s * 0.25);
      g.lineTo(cx - s * 0.7, cy + s * 0.45);
      g.lineTo(cx - s, cy - s * 0.35);
      g.lineTo(cx - s * 0.55, cy - s * 0.1);
      g.closePath();
      g.fill();
      g.fillRect(cx - 0.012 * w, cy + s * 0.2, 0.024 * w, s * 0.55);
      break;
    }
    case 'taeguk': {
      const r = 0.155 * w;
      g.fillStyle = css(e.top);
      g.beginPath();
      g.arc(cx, cy, r, Math.PI, 0);
      g.fill();
      g.fillStyle = css(e.bottom);
      g.beginPath();
      g.arc(cx, cy, r, 0, Math.PI);
      g.fill();
      // M153: the S-curve (two half-size discs), not a straight split.
      g.fillStyle = css(e.top);
      g.beginPath();
      g.arc(cx - r / 2, cy, r / 2, 0, Math.PI * 2);
      g.fill();
      g.fillStyle = css(e.bottom);
      g.beginPath();
      g.arc(cx + r / 2, cy, r / 2, 0, Math.PI * 2);
      g.fill();
      break;
    }
    case 'quarters': {
      const [tl, tr, bl, br] = e.colors;
      g.fillStyle = css(tl);
      g.fillRect(0, 0, cx, cy);
      g.fillStyle = css(tr);
      g.fillRect(cx, 0, cx, cy);
      g.fillStyle = css(bl);
      g.fillRect(0, cy, cx, cy);
      g.fillStyle = css(br);
      g.fillRect(cx, cy, cx, cy);
      break;
    }
    case 'shield': {
      paintShield(g, e.arms, cx, cy, w);
      break;
    }
    case 'sun': {
      g.fillStyle = css(e.color);
      const r = 0.11 * w;
      for (let i = 0; i < 8; i++) {
        const a = (i * Math.PI) / 4;
        g.beginPath();
        g.moveTo(cx + Math.cos(a - 0.14) * r, cy + Math.sin(a - 0.14) * r);
        g.lineTo(cx + Math.cos(a) * r * 2.4, cy + Math.sin(a) * r * 2.4);
        g.lineTo(cx + Math.cos(a + 0.14) * r, cy + Math.sin(a + 0.14) * r);
        g.closePath();
        g.fill();
      }
      g.beginPath();
      g.arc(cx, cy, r, 0, Math.PI * 2);
      g.fill();
      break;
    }
    case 'bar': {
      g.fillStyle = css(e.color);
      g.fillRect(0.22 * w, 0.56 * hgt, 0.56 * w, 0.07 * hgt);
      break;
    }
    case 'stars': {
      g.fillStyle = css(e.color);
      for (const [x, y, r, pts] of e.pts) {
        starPath(g, x * w, y * hgt, r * hgt, pts ?? 5);
        g.fill();
      }
      break;
    }
    case 'tri4': {
      const [top, fly, bottom, hoist] = e.colors;
      const tri = (col: number, ax: number, ay: number, bx: number, by: number): void => {
        g.fillStyle = css(col);
        g.beginPath();
        g.moveTo(ax, ay);
        g.lineTo(bx, by);
        g.lineTo(cx, cy);
        g.closePath();
        g.fill();
      };
      tri(top, 0, 0, w, 0);
      tri(fly, w, 0, w, hgt);
      tri(bottom, w, hgt, 0, hgt);
      tri(hoist, 0, hgt, 0, 0);
      break;
    }
  }
}

/** Paint a FlagSpec's bands + (a subset of) its emblems into a context. */
function paintFlag(
  g: CanvasRenderingContext2D,
  spec: FlagSpec,
  w: number,
  hgt: number,
  emblems: 'all' | 'stretch',
): void {
  const weights = spec.weights ?? spec.colors.map(() => 1);
  const total = weights.reduce((a, b) => a + b, 0);
  let at = 0;
  spec.colors.forEach((color, i) => {
    const frac = (weights[i] ?? 1) / total;
    g.fillStyle = css(color);
    if (spec.pattern === 'h') g.fillRect(0, at * hgt, w, frac * hgt + 1);
    else g.fillRect(at * w, 0, frac * w + 1, hgt);
    at += frac;
  });
  for (const e of spec.emblems ?? []) {
    if (emblems === 'all' || isStretchEmblem(e)) drawEmblem(g, e, w, hgt);
  }
}

/** Rasterise a FlagSpec (flag-shaped — the shop cards' art). */
export function flagCanvas(spec: FlagSpec, w = 512, hgt = 512): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = hgt;
  const g = c.getContext('2d')!;
  paintFlag(g, spec, w, hgt, 'all');
  return c;
}

/** M102 — emblem taxonomy for the WRAP layout: FIELD patterns (crosses,
 *  saltires, cantons…) stretch with the flag across the whole flank, but
 *  BADGE crests (discs, suns, checkers…) must stay round — stretching them
 *  down a 4 m body was exactly the owner's "flags don't translate" report. */
function isStretchEmblem(e: FlagEmblem): boolean {
  if (e.kind === 'cross') return !e.plump;
  return (
    e.kind === 'nordic' ||
    e.kind === 'saltire' ||
    e.kind === 'diag' ||
    e.kind === 'canton' ||
    e.kind === 'triangle' ||
    e.kind === 'quarters' ||
    e.kind === 'tri4' ||
    e.kind === 'pall'
  );
}

// ---------------------------------------------------------------------------
// M127 — LIVERY FIT. Every livery canvas is a SIDE-VIEW painting of the
// vehicle's PAINTED BODY: canvas x runs nose (left) → tail (right) over the
// painted length, canvas y runs roof (top) → the lowest painted point
// (bottom). The old projection normalised against the WHOLE vehicle — tyres
// and suspension included — so on the monster truck (shell perched on 1.3 m
// of axles and tyres) the flag's bands landed in thin air under the body and
// the shell wore nothing but the top colour (the owner's Netherlands report).
// Now `measureLivery` finds the painted body itself, and the painter is told
// two numbers about it: where the BELT line (glass) sits, so collars, roof
// zones and chest bands land where a shirt's would on every ride, and the
// body's length ÷ height, so round crests stay round.
// ---------------------------------------------------------------------------

/** How a livery canvas maps onto ONE vehicle's painted body. */
export interface LiveryFit {
  /** Where the glass starts, as a fraction of the painted body's height from
   *  its lowest painted point (≈0.62 coupe, ≈0.73 sports car, ≈0.53 monster
   *  truck). Everything above is the roof/greenhouse zone (the "sleeves"). */
  belt: number;
  /** Painted length ÷ painted height (≈3.2 monster truck … ≈7.8 Formula). */
  aspect: number;
}

/** The fit the generic textures are painted for: the procedural placeholder
 *  body, the bulldozer form, and any caller with no measured model. */
export const DEFAULT_FIT: LiveryFit = { belt: 0.62, aspect: 4 };

/** What a body wears — a flag wrap OR a kit paint job (the M87 one-slot rule). */
export interface LiverySpec {
  id: string;
  flag?: FlagSpec;
  kit?: KitSpec;
}

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);

/** A BADGE emblem's own footprint inside the square it is drawn into (the
 *  fraction of the canvas the drawEmblem shape spans), so every crest can be
 *  scaled to the SAME on-car height whatever its intrinsic drawing size. */
function badgeExtent(e: FlagEmblem): number {
  switch (e.kind) {
    case 'disc':
      return 2 * (e.r ?? 0.12);
    case 'star':
      return 0.22;
    case 'crescent':
      return e.disc !== undefined ? 0.4 : 0.26;
    case 'cross':
      return 0.36;
    case 'diamond':
      return 0.8;
    case 'checker':
      return 0.25;
    case 'leaf':
      return 0.42;
    case 'taeguk':
      return 0.31;
    case 'sun':
      return 0.53;
    case 'bar':
      return 0.56;
    case 'shield':
      return 0.52;
    case 'wyvern':
      return 0.52;
    default:
      return 0.3;
  }
}

/** M153: where a BADGE's drawing is centred inside its square (as a fraction
 *  of the square's height). Most are drawn about the centre; the checker sits
 *  in the upper part (the flag card's crest position) and the bar low, so a
 *  crest scaled up for the car was placed one crest-height too high (Croatia's
 *  checkerboard landed under the window line, measured on the lab sheets). */
function badgeCentreY(e: FlagEmblem): number {
  if (e.kind === 'checker') return 0.18 + (4 * 0.05) / 2;
  if (e.kind === 'bar') return 0.595;
  return 0.5;
}

/** Draw `paint` into a square scratch canvas and blit it centred on (cx, cy)
 *  `hb` px tall, with the width aspect-corrected so the square lands as a
 *  SQUARE on the body (a 2:1 canvas over a 4:1 body is otherwise 2× wide). */
function blitSquare(
  g: CanvasRenderingContext2D,
  w: number,
  hgt: number,
  fit: LiveryFit,
  cx: number,
  cy: number,
  hb: number,
  paint: (bg: CanvasRenderingContext2D, size: number) => void,
): void {
  const size = 512;
  const bc = document.createElement('canvas');
  bc.width = bc.height = size;
  paint(bc.getContext('2d')!, size);
  const wb = (hb * (w / hgt)) / fit.aspect;
  g.drawImage(bc, cx - wb / 2, cy - hb / 2, wb, hb);
}

/** M154 polish: a crest's room over an up-facing shoulder (the fold map's
 *  share of it) up to this much is still seen whole: a sliver of shoulder
 *  at a crest's edge, which every car's door crest touches, costs nothing. */
export const FOLD_FREE = 0.1;
/** M154 polish: a crest's room on a SECOND surface a big step in or out
 *  from its main one (offLayerShare) up to this much is still seen whole. */
export const LAYER_FREE = 0.05;
/** M154 polish r2: a star's footprint over a surface the side painting is
 *  smeared across (smearCover) up to this much is still seen whole. */
export const SMEAR_FREE = 0.04;
/** M154 polish r2: a crest's smallest readable height on the body (metres),
 *  the clear paint kept either side of it along the car (metres), and the
 *  share of it that must be seen before it settles near its own place
 *  rather than move to a bigger clear patch along the body. A canton's lone
 *  emblem (Uruguay's sun, Chile's star) smaller than CANTON_EMBLEM_MIN_M in
 *  the stretch of canton the side shows leaves the canton for such a patch,
 *  on a disc of the canton's colour. */
export const CREST_MIN_M = 0.2;
export const CREST_RING = 0.06;
export const CREST_SEEN = 0.75;
export const CANTON_EMBLEM_MIN_M = 0.15;

/** M154 polish r4 (pure; the tests pin it): the share of the box x0..x1 ×
 *  y0..y1 (side-view metres) inside the UNION of the mirrors' rects (each
 *  mirror is several parts, on both sides of the car: summing their
 *  overlaps counted the same paint up to six times), sampled on a grid. */
export function mirrorShare(mirrors: readonly { x0: number; x1: number; y0: number; y1: number }[], x0: number, x1: number, y0: number, y1: number): number {
  if (!mirrors.length || x1 <= x0 || y1 <= y0) return 0;
  const n = 12;
  let hit = 0;
  for (let i = 0; i < n; i++) {
    const x = x0 + ((i + 0.5) / n) * (x1 - x0);
    for (let j = 0; j < n; j++) {
      const y = y0 + ((j + 0.5) / n) * (y1 - y0);
      if (mirrors.some((m) => x >= m.x0 && x <= m.x1 && y >= m.y0 && y <= m.y1)) hit++;
    }
  }
  return hit / (n * n);
}

/** M154 polish r4: Uruguay's sun drawn smaller than CANTON_EMBLEM_SMALL_M
 *  (metres of the body: the rebuilt sports car's sun, a dot by its arch,
 *  against the coupe's 0.23 and the muscle car's 0.2), or held by the
 *  canton's visible run under CANTON_EMBLEM_SQUEEZE of the canton's height
 *  (its natural size is 0.6 of it: today's sports car's came out at 0.51, a
 *  12 px sun against the live build's 21), or with more than
 *  CANTON_MIRROR_HIDE of it on paint a side mirror hides from the angled
 *  views (the rebuilt pickup's), moves to the nearest clear patch of its
 *  canton no mirror hides, at no less than CANTON_EMBLEM_LIVE_M (the live
 *  build's sun on the old sports car and pickup, measured: 21-22 px in the
 *  lab's side sheet) and at no less than CANTON_MOVE_KS of that: never
 *  smaller. (The sun only: Chile's lone star is not on this round's list.) */
export const CANTON_EMBLEM_SMALL_M = 0.18;
export const CANTON_EMBLEM_SQUEEZE = 0.55;
export const CANTON_EMBLEM_LIVE_M = 0.25;
export const CANTON_MIRROR_HIDE = 0.35;
export const CANTON_MOVE_KS: readonly number[] = [1, 0.95];
/** the sizes a crest may shrink to in its search (of its own height) */
const CREST_KS: readonly number[] = [1, 0.88, 0.76, 0.64, 0.52];

/** M154 polish r4 (pure; the tests pin it): whether a canton's sun of
 *  height h metres, `squeeze` of its canton's height, with mirrorHide of it
 *  hidden by a mirror, moves. */
export function cantonEmblemMoves(h: number, mirrorHide: number, squeeze = 0.6): boolean {
  return h < CANTON_EMBLEM_SMALL_M || squeeze < CANTON_EMBLEM_SQUEEZE || mirrorHide > CANTON_MIRROR_HIDE;
}

/** M154 polish r4: the Formula's centred crests: below CREST_KEEP_LIVE of
 *  their size (the band's share as drawn live) after the band search, they
 *  look for a place up the engine cover's flank (CREST_COVER_DYS of the
 *  painting's height above their place, CREST_COVER_REACH along the car:
 *  only UP, as low on the monocoque under the cockpit the rear
 *  three-quarter view saw half of it behind the halo) at
 *  the sizes CREST_COVER_KS, seen whole (CREST_WHOLE of them and of their
 *  centre on clear paint). */
export const CREST_KEEP_LIVE = 0.95;
export const CREST_COVER_KS: readonly number[] = [1, 0.97];
export const CREST_COVER_REACH = 0.3;
export const CREST_COVER_DYS: readonly number[] = Array.from({ length: 21 }, (_, i) => -0.46 + i * 0.0175);
export const CREST_WHOLE = 0.95;
/** M154 polish r4: ...and no more than this share of it over an up-facing
 *  shoulder: a few percent of the side painting there is a red patch
 *  across the whole top of a sidepod seen from above and behind. */
export const CREST_COVER_FOLD = 0.01;
/** M154 polish r4: the flank kept clear over the crest's top, as a share of
 *  its height. */
export const CREST_COVER_TOP = 0.08;

/** M154 polish r4 (pure; the tests pin it): whether a crest at `bh` of its
 *  `crestH` looks for the engine cover: only on a ride with an open cockpit
 *  and no cabin (the Formula), and only below CREST_KEEP_LIVE of its size. */
export function crestOnCover(fit: LiveryFit | LiveryMeasure, bh: number, crestH: number): boolean {
  return 'cockpit' in fit && !!fit.cockpit && !('cabin' in fit && fit.cabin) && bh < CREST_KEEP_LIVE * crestH - 1e-6;
}

/** M154 polish: the share of a crest's room that still counts as seen when
 *  `fold` of it lies over an up-facing shoulder and `layer` of it on a
 *  second surface (pure; the tests pin it). A crest split between two
 *  surfaces reads as two halves from every view but the side, so that costs
 *  much more than a shoulder at its edge. */
export function foldKeep(fold: number, layer = 0): number {
  const f = fold <= FOLD_FREE ? 1 : Math.max(0, 1 - (2 * (fold - FOLD_FREE)) / (1 - FOLD_FREE));
  const l = layer <= LAYER_FREE ? 1 : Math.max(0, 1 - (4 * (layer - LAYER_FREE)) / (1 - LAYER_FREE));
  return f * l;
}

/**
 * M154 polish (pure; the tests pin it): the stretch of a canton its stars go
 * on — the longest run of columns `colW` wide between x0 and x1 where
 * `cover` (over 80% of the strip's height) is 0.55 or more. The canton's
 * full height first, as before; when no run a quarter of the canton long is
 * seen at full height (the Formula's low nose lies in its middle rows), the
 * strip of it, a little lower or higher and shorter, with the most visible
 * area. Null: nothing of it is seen (the stars keep the whole canton).
 */
export function cantonStarStrip(
  cover: (x: number, y: number, bw: number, bh: number) => number,
  x0: number,
  x1: number,
  y0: number,
  y1: number,
  colW: number,
): { x0: number; x1: number; y0: number; y1: number } | null {
  const all = cantonStarStrips(cover, x0, x1, y0, y1, colW);
  if (!all.length) return null;
  if (all[0]!.y0 === y0 && all[0]!.y1 === y1) return all[0]!;
  let out: { x0: number; x1: number; y0: number; y1: number } | null = null;
  let area = 0;
  for (const r of all) {
    const a = (r.x1 - r.x0) * (r.y1 - r.y0);
    if (a > area + 1e-9) {
      area = a;
      out = r;
    }
  }
  return out;
}

/**
 * M154 polish r3 (pure; the tests pin it): every stretch of a canton its
 * stars may go on, the full-height one first (cantonStarStrip's rule): the
 * longest seen run of columns at the canton's full height, then, a little
 * lower or higher and shorter, each such run at least a quarter of the canton
 * long. The caller lays the stars out on each and keeps the one whose stars
 * are biggest with every star clear (cantonStarLayout).
 */
export function cantonStarStrips(
  cover: (x: number, y: number, bw: number, bh: number) => number,
  x0: number,
  x1: number,
  y0: number,
  y1: number,
  colW: number,
): { x0: number; x1: number; y0: number; y1: number }[] {
  const run = (sy0: number, sy1: number): [number, number] | null => {
    const sh = sy1 - sy0;
    let best: [number, number] | null = null;
    let run0 = -1;
    for (let c = x0; c <= x1 + 1e-6; c += colW) {
      const ok = c + colW <= x1 + 1e-6 && cover(c + colW / 2, sy0 + sh / 2, colW, sh * 0.8) >= 0.55;
      if (ok && run0 < 0) run0 = c;
      if (!ok && run0 >= 0) {
        if (!best || c - run0 > best[1] - best[0]) best = [run0, c];
        run0 = -1;
      }
    }
    return best;
  };
  const need = 0.25 * (x1 - x0);
  const out: { x0: number; x1: number; y0: number; y1: number }[] = [];
  const full = run(y0, y1);
  if (full && full[1] - full[0] >= need) out.push({ x0: full[0], x1: full[1], y0, y1 });
  const h = y1 - y0;
  for (const f of [0.85, 0.75, 0.6, 0.45]) {
    for (let o = 0; o <= 1 - f + 1e-9; o += 0.05) {
      const sy0 = y0 + o * h;
      const sy1 = sy0 + f * h;
      const r = run(sy0, sy1);
      if (!r || r[1] - r[0] < need) continue;
      out.push({ x0: r[0], x1: r[1], y0: sy0, y1: sy1 });
    }
  }
  return out;
}

/**
 * M154 polish r3 (pure; the tests pin it): a canton's `ns` stars laid out on
 * a stretch of it (canvas units; `toX` turns a height into the same width):
 * the grid follows the stretch's shape in metres (a long canton gets more
 * columns, so its stars stay a readable size), `minRows` rows at least; one
 * star (or the sun) sits in the middle, as big as the stretch allows.
 */
export function cantonStarLayout(
  st: { x0: number; x1: number; y0: number; y1: number },
  ns: number,
  toX: number,
  minRows = 1,
  /** M154 polish r4: the star's height as a share of its cell. */
  fill = 0.62,
  /** (polish r5: this many rows instead of the squarest grid's) */
  rowsAt?: number,
): { pts: { x: number; y: number }[]; h: number } {
  const gw = st.x1 - st.x0;
  const sh = st.y1 - st.y0;
  if (ns <= 1) return { pts: [{ x: (st.x0 + st.x1) / 2, y: (st.y0 + st.y1) / 2 }], h: Math.min(0.6 * sh, (0.8 * gw) / toX) };
  const aspectM = gw / toX / sh;
  const rows = rowsAt ?? Math.max(minRows, Math.min(4, Math.round(Math.sqrt(ns / aspectM))));
  const cols = Math.ceil(ns / rows);
  const cellH = sh / rows;
  const cellW = gw / cols;
  const pts: { x: number; y: number }[] = [];
  for (let i = 0; i < ns; i++) pts.push({ x: st.x0 + ((i % cols) + 0.5) * cellW, y: st.y0 + (Math.floor(i / cols) + 0.5) * cellH });
  return { pts, h: fill * Math.min(cellH, cellW / toX) };
}

/**
 * M154 polish r3 (pure; the tests pin it): of a canton's stretches, the one
 * whose stars come out BIGGEST with no star on a smeared surface and every
 * star in sight (a grid of six or more may lose one star behind an arch or
 * a seam, as the live build's did); the first stretch (the canton's full
 * height, the live build's layout) wins a tie. `judge` rates one star's
 * footprint: below 0 = smeared, else the share of it the side sees. Round 2
 * took the stretch whose every COLUMN was clear of the smear map top to
 * bottom, so a shoulder crease above the stars cut the coupe's USA canton
 * from three rows of big stars to two rows of small ones and shrank
 * Uruguay's sun. Null when no stretch passes.
 */
export function bestCantonLayout(
  strips: readonly { x0: number; x1: number; y0: number; y1: number }[],
  ns: number,
  toX: number,
  minRows: number,
  judge: (x: number, y: number, h: number) => number,
  fill = 0.62,
  /** (polish r5: every row count from minRows to 4, not just the squarest) */
  anyRows = false,
): { strip: { x0: number; x1: number; y0: number; y1: number }; pts: { x: number; y: number }[]; h: number } | null {
  let best: { strip: { x0: number; x1: number; y0: number; y1: number }; pts: { x: number; y: number }[]; h: number } | null = null;
  const lose = ns >= 6 ? 1 : 0;
  const layouts = (strip: { x0: number; x1: number; y0: number; y1: number }): { pts: { x: number; y: number }[]; h: number }[] =>
    anyRows
      ? Array.from({ length: Math.max(0, 5 - minRows) }, (_, i) => cantonStarLayout(strip, ns, toX, minRows, fill, minRows + i))
      : [cantonStarLayout(strip, ns, toX, minRows, fill)];
  for (const strip of strips) for (const L of layouts(strip)) {
    if (best && L.h <= best.h * 1.02) continue;
    let ok = true;
    let hidden = 0;
    for (const p of L.pts) {
      const v = judge(p.x, p.y, L.h);
      if (v < 0) ok = false;
      else if (v < 0.5) hidden++;
    }
    if (ok && hidden <= lose) best = { strip, ...L };
  }
  return best;
}

/** M154 polish r4: a canton whose smear-free star grid comes out below
 *  CANTON_GROW_BELOW of the live build's grows, step by step, until the grid
 *  reaches CANTON_GROW_TO of the live size: back along the flank (`grow`
 *  times its own length, its stars on its own length at the far end), then a
 *  stripe deeper too (`deep`), then with the stars a little closer set
 *  (`fill` of their cells, 0.62 as drawn). */
export const CANTON_GROW_BELOW = 0.75;
export const CANTON_GROW_TO = 0.95;
/** M154 polish r5: the rides whose canton stars came out under the live
 *  build's (the pickup, both bodies: 55-60% of live's star area; the monster
 *  truck: 90%). There the grid grows whenever it is short of the live size
 *  (CANTON_LIVE_BELOW) until it reaches it (CANTON_LIVE_TO), and every star
 *  stands clear of a panel gap (BodyFit.seams) and wholly on paint the side
 *  sees (CANTON_LIVE_SEEN: an arch's flare clipped one on the new pickup). */
export const CANTON_LIVE_RIDES: ReadonlySet<string> = new Set(['pickup', 'van']);
/** The live build's star, as a share of the live grid laid on today's
 *  canton (the grid's own measure): measured off the live renders, the old
 *  pickup's stars were 11 px where that grid gives 9 (its taller canton). */
export const CANTON_LIVE_TO: Readonly<Record<string, number>> = { pickup: 1.25, van: 1.0 };
export const CANTON_LIVE_SEEN = 0.9;

/**
 * (pure; the tests pin it) The growth steps for a CANTON_LIVE_RIDE: the
 * canton grows back along the flank only as far as the first panel gap
 * behind it (`maxGrow`, its own length's share: it never spans two doors),
 * a stripe deeper, and with the stars a little closer set; in that order.
 */
export function liveCantonSteps(maxGrow: number): CantonStep[] {
  const grows = [1, 1.1, 1.2, 1.3, 1.4, 1.5, 1.6].filter((g) => g <= maxGrow + 1e-9);
  if (maxGrow > 1 && maxGrow < 1.6 && !grows.some((g) => Math.abs(g - maxGrow) < 0.02)) grows.push(maxGrow);
  const out: CantonStep[] = [];
  for (const fill of [0.62, 0.76, 0.86]) for (const deep of [0, 1]) for (const grow of grows) out.push({ grow, deep, fill });
  return out;
}

/** (pure; the tests pin it) Whether a star's footprint (side-view metres:
 *  centre x, y, width w, height h) crosses a panel gap, with `pad` of clear
 *  paint kept between them. */
export function starOnSeam(seams: readonly { x: number; y0: number; y1: number }[], x: number, y: number, w: number, h: number, pad = 0.01): boolean {
  return seams.some((s) => Math.abs(s.x - x) < w / 2 + pad && s.y1 > y - h / 2 && s.y0 < y + h / 2);
}
export interface CantonStep {
  grow: number;
  deep: number;
  fill: number;
}
export const CANTON_GROW_STEPS: readonly CantonStep[] = [
  ...[1.1, 1.2, 1.3, 1.4, 1.5, 1.6].map((grow) => ({ grow, deep: 0, fill: 0.62 })),
  ...[1, 1.2, 1.4, 1.6].map((grow) => ({ grow, deep: 1, fill: 0.62 })),
  ...[1, 1.2, 1.4, 1.6].map((grow) => ({ grow, deep: 1, fill: 0.76 })),
];

/**
 * M154 polish r4 (pure; the tests pin it): how a canton grows so its stars,
 * kept off the smear, are not much smaller than the live build's (`liveH` =
 * the live grid's star height, `clearH` = the clear grid's in the canton as
 * drawn, 0 when there is none): null when the clear grid already holds
 * CANTON_GROW_BELOW of the live size, else the first step whose clear grid
 * (`layoutAt(step)`) reaches CANTON_GROW_TO of it (the largest one seen when
 * none does, if it is bigger than the canton's own; null when none is).
 */
export function cantonGrowth<T extends { h: number }>(
  liveH: number,
  clearH: number,
  layoutAt: (step: CantonStep) => T | null,
  /** (polish r5: the shares and steps for the CANTON_LIVE_RIDES) */
  below = CANTON_GROW_BELOW,
  to = CANTON_GROW_TO,
  steps: readonly CantonStep[] = CANTON_GROW_STEPS,
): { step: CantonStep; layout: T } | null {
  if (!(liveH > 0) || clearH >= below * liveH) return null;
  let best: { step: CantonStep; layout: T } | null = null;
  for (const step of steps) {
    const l = layoutAt(step);
    if (!l) continue;
    if (l.h >= to * liveH) return { step, layout: l };
    if (l.h > (best?.layout.h ?? clearH) * 1.02) best = { step, layout: l };
  }
  return best;
}

/**
 * The WRAP texture (M102, refit M127, M153): a SIDE-VIEW livery painting of
 * the flag over the painted body. The flag is a billboard on the METAL: the
 * band from just above the belt line (the base of the glass) down to the sill
 * is the only part of every body that reads from the side, so
 *   - horizontal flags (single-colour fields included) paint their whole
 *     field into that band; the top colour carries on over the roof and the
 *     bottom colour under the sill. A canton lands on the front fender, not
 *     in the air over a low nose (Australia, DR Congo, Chile, Uruguay);
 *   - vertical flags run their bands the full height (France: blue nose,
 *     white, red tail), and their crests sit in the same metal band;
 *   - every crest is placed ROUND, centred and sized INSIDE that band, so the
 *     glass never clips it (M153: Japan's sun, the Swiss cross, Korea's
 *     taeguk, Canada's leaf were centred on the whole body height at 68% of
 *     it, so their tops always ran into the windows), and slid clear of a
 *     wheel arch when the body is known (Panama's red star sat on the rear
 *     arch).
 * The fitted models put the NOSE at u=0 (canvas left), so the flag reads as
 * flying off the front of the car.
 */
export function wrapCanvas(
  spec: FlagSpec,
  fit: LiveryFit | LiveryMeasure = DEFAULT_FIT,
  w = 1024,
  hgt = 512,
  /** (polish r5: what the plan-view decal needs, filled in as it paints) */
  extras?: WrapExtras,
): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = hgt;
  const g = c.getContext('2d')!;
  const roofLine = 1 - fit.belt; // the belt line, as a canvas-y fraction
  const horizontal = spec.pattern === 'h';
  // The METAL band every crest lives in (and every horizontal field): from
  // a touch above the belt (so the shoulder under the glass is covered) to
  // just short of the lowest painted point.
  const top = Math.max(0.02, roofLine - 0.06);
  const bot = 0.94;
  g.fillStyle = css(spec.colors[0]);
  g.fillRect(0, 0, w, hgt);
  if (horizontal) {
    g.fillStyle = css(spec.colors[spec.colors.length - 1]);
    g.fillRect(0, bot * hgt, w, hgt - bot * hgt);
  }

  // A saltire alone on its field (Scotland) is drawn in METRES on the door:
  // stretched along a 4 m flank it flattened into two pinstripes crossing at
  // 8 degrees (re-review). With triangles or quarters (Jamaica) it stays in
  // the field, corner to corner of the pattern it divides.
  const loneSaltire =
    horizontal &&
    (spec.emblems ?? []).some((e) => e.kind === 'saltire') &&
    !(spec.emblems ?? []).some((e) => e.kind === 'tri4' || e.kind === 'quarters');
  // Field pass: bands + stretchable emblems. Cantons and crosses are NOT
  // drawn here: stretched along a 4 m body a canton is a 2 m strip with its
  // stars smeared, and a cross's horizontal arm (a share of the band's
  // height) shrinks to a pinstripe beside an upright 5x its width (measured
  // on the review sheets). Both are drawn below in METRES.
  const fieldSpec: FlagSpec = {
    ...spec,
    emblems: spec.emblems?.filter(
      (e) =>
        e.kind !== 'canton' &&
        e.kind !== 'nordic' &&
        !(e.kind === 'cross' && !e.plump) &&
        !(e.kind === 'saltire' && loneSaltire),
    ),
  };
  const field = document.createElement('canvas');
  field.width = field.height = 512;
  paintFlag(field.getContext('2d')!, fieldSpec, 512, 512, 'stretch');
  if (horizontal) g.drawImage(field, 0, top * hgt, w, (bot - top) * hgt);
  else g.drawImage(field, 0, 0, w, hgt);

  const fieldH = (bot - top) * hgt;
  const atY = (ay: number): number => (top + ay * (bot - top)) * hgt;
  const toX = (w / hgt) / fit.aspect; // vertical px -> horizontal px, same metres
  // A centred crest sits on the DOOR: midway between the arches (and the
  // side scoop, when the body has one), not at a fixed share of the length.
  let doorMid = 0.55;
  if ('wheels' in fit && fit.wheels.length >= 2) {
    const f = fit.wheels[0];
    const r = fit.wheels[fit.wheels.length - 1];
    const d0 = f.x + f.r * 1.1;
    const d1 = Math.min(r.x - r.r * 1.1, fit.scoop ? fit.scoop.x0 : Infinity);
    if (d1 > d0) doorMid = (d0 + d1) / 2 / fit.L;
  }
  // The pickup is a crew cab: between its arches lies the B-pillar door gap,
  // which ran down the middle of every centred crest (round 3). Its crests
  // sit on the FRONT door.
  if ('cabin' in fit && fit.cabin && fit.vehicle === 'pickup') {
    const d0 = fit.cabin.x0 + 0.12;
    doorMid = (d0 + (d0 + fit.cabin.x1) / 2) / 2 / fit.L;
  }
  // M153: a ride with no cabin (the bike) has its RIDER between the wheels:
  // the crests aim at the longest stretch of paint that can be seen from the
  // side (the fairing), not at the rider's knee (round 2).
  if ('flankClear' in fit && fit.flankClear && !fit.cabin && !fit.cockpit) {
    const seen = { ...fit, flank: fit.flankClear };
    const y0 = top * fit.H;
    const y1 = bot * fit.H;
    const step = fit.L / fit.flankClear.cols;
    let best: [number, number] | null = null;
    let run0 = -1;
    for (let c = 0; c <= fit.flankClear.cols; c++) {
      const ok = c < fit.flankClear.cols && flankCover(seen, c * step, (c + 1) * step, y0, y1) >= 0.6;
      if (ok && run0 < 0) run0 = c;
      if (!ok && run0 >= 0) {
        if (!best || c - run0 > best[1] - best[0]) best = [run0, c];
        run0 = -1;
      }
    }
    if (best && (best[1] - best[0]) * step > 0.15 * fit.L) doorMid = ((best[0] + best[1]) / 2) * step / fit.L;
  }

  for (const e of spec.emblems ?? []) {
    if (!(e.kind === 'saltire' && loneSaltire)) continue;
    // In METRES (x scaled by toX), 2.3:1 like the real saltire over the door,
    // and the arms run on past the band (over the roof, under the sill)
    // instead of stopping square at the door's edges like a sticker.
    const ch = fieldH;
    g.save();
    g.translate(doorMid * w, atY(0.5));
    g.scale(toX, 1);
    g.strokeStyle = css(e.color);
    g.lineWidth = 0.24 * ch;
    g.lineCap = 'butt';
    const run = 2.6; // how far past the band the arms carry, in band heights
    g.beginPath();
    g.moveTo(-1.15 * ch * run, -0.5 * ch * run);
    g.lineTo(1.15 * ch * run, 0.5 * ch * run);
    g.moveTo(1.15 * ch * run, -0.5 * ch * run);
    g.lineTo(-1.15 * ch * run, 0.5 * ch * run);
    g.stroke();
    g.restore();
  }
  // Cross pass (England, the Nordic crosses): both arms the same width in
  // METRES, the horizontal arm along the middle of the metal band, the
  // upright the FULL height so it crosses the roof and reads from above and
  // behind (the roof had turned plain field colour, the review's regression).
  for (const e of spec.emblems ?? []) {
    if (!(e.kind === 'nordic' || (e.kind === 'cross' && !e.plump))) continue;
    const nordic = e.kind === 'nordic';
    let ux = (nordic ? 0.36 : doorMid) * w;
    // M153: the upright is the cross on the ROOF too; at 0.36 of the length
    // it fell on the windscreen (glass, so Denmark's roof read plain red).
    // It slides BACK (never forward: round 3 let it onto the coupe's cowl,
    // where the gill slit cut every Nordic cross) to the nearest stretch of
    // roof that is paint, with no slit or vent across its run down the side.
    if (nordic && 'plan' in fit && fit.plan) {
      const xm = 0.36 * fit.L;
      const tm = 0.24 * (fieldH / hgt) * fit.H;
      const y0 = (top + 0.15 * (bot - top)) * fit.H;
      const y1 = (top + 0.85 * (bot - top)) * fit.H;
      const sideOk = (x: number): boolean => !('flank' in fit) || !fit.flank || flankCover(fit, x - tm / 2, x + tm / 2, y0, y1) >= 0.8;
      const roofOk = (x: number): boolean => planCover(fit, x - tm / 2, x + tm / 2, fit.W * 0.3, fit.W * 0.7) >= 0.6;
      let best = NaN;
      for (let x = xm; x <= xm + 0.9 && Number.isNaN(best); x += 0.03) if (roofOk(x) && sideOk(x)) best = x;
      // No paint roof back there: at least keep the side's run clear, as near
      // the windscreen pillar as it can be.
      for (let d = 0; d <= 0.6 && Number.isNaN(best); d += 0.03) if (sideOk(xm + d)) best = xm + d;
      if (!Number.isNaN(best)) ux = (best / fit.L) * w;
    }
    const midY = atY(0.5);
    const bars: [number, number][] =
      nordic && e.outline !== undefined ? [[e.outline, 0.36], [e.color, 0.2]] : [[e.color, nordic ? 0.24 : 0.26]];
    for (const [col, t] of bars) {
      const th = t * fieldH;
      g.fillStyle = css(col);
      g.fillRect(0, midY - th / 2, w, th);
      const tw = th * toX;
      g.fillRect(ux - tw / 2, 0, tw, hgt);
    }
  }
  // Wheel arches in canvas pixels (a known body only): a crest slides along
  // the car until it is clear of them.
  const arches = 'wheels' in fit
    ? fit.wheels.map((wh) => ({ x: (wh.x / fit.L) * w, y: (wh.y / fit.H) * hgt, rx: (wh.r * 1.18 * w) / fit.L, ry: (wh.r * 1.18 * hgt) / fit.H }))
    : [];
  // The side scoop (sports): a crest never sits in its dark mouth.
  const scoopPx = 'scoop' in fit && fit.scoop ? [(fit.scoop.x0 / fit.L) * w, (fit.scoop.x1 / fit.L) * w] : null;
  const clearOfArches = (cx: number, cy: number, bw: number, bh: number): number => {
    const hits = (x: number): boolean =>
      (scoopPx !== null && x + bw / 2 > scoopPx[0] && x - bw / 2 < scoopPx[1]) ||
      arches.some((a) => {
        // the badge box's nearest point to the arch centre, in arch-normalised units
        const nx = Math.max(x - bw / 2, Math.min(a.x, x + bw / 2));
        const ny = Math.max(cy - bh / 2, Math.min(a.y, cy + bh / 2));
        return ((nx - a.x) / a.rx) ** 2 + ((ny - a.y) / a.ry) ** 2 < 1;
      });
    if (!hits(cx)) return cx;
    const dir = cx < w / 2 ? 1 : -1; // toward the doors
    for (let k = 1; k <= 60; k++) {
      const x = cx + dir * k * (w / 200);
      if (!hits(x)) return x;
    }
    return cx;
  };
  // M153: on a measured body the crest SEARCHES around its place for metal
  // (the flank map: no scoop, window, arch or cut cavity under it), at full
  // size first and a little smaller if that is what keeps it whole.
  const mapped = 'flank' in fit && fit.flank ? fit : null;
  // A tyre stands in front of the paint the flank map sees round an arch:
  // a crest overlapping a wheel counts as mostly off the metal (Panama's red
  // star sat half behind the rear tyre, round 2).
  const onWheel = (x: number, y: number, bw: number, bh: number): boolean =>
    arches.some((a) => {
      const nx = Math.max(x - bw / 2, Math.min(a.x, x + bw / 2));
      const ny = Math.max(y - bh / 2, Math.min(a.y, y + bh / 2));
      return ((nx - a.x) / (a.rx * 0.9)) ** 2 + ((ny - a.y) / (a.ry * 0.9)) ** 2 < 1;
    });
  // ...and it reads the VISIBLE-paint map: the bike's rider, tyres, door
  // handles and steps stand in front of the paint (crests sat behind the
  // rider's knee and the Formula's front tyre, round 2).
  const seen = mapped && mapped.flankClear ? { ...mapped, flank: mapped.flankClear } : mapped;
  // M154 polish: ...and a crest lying largely over an up-facing SHOULDER (a
  // sidepod top, a low nose: the fold map), where the side painting is
  // stretched along it, or across TWO surfaces that only line up from the
  // side (a sidepod's wall and the engine cover's flank behind it), counts as
  // unseen: the Formula's Japan disc and Brazil diamond folded over its
  // sidepod shoulder, Argentina's sun was cut in half there and its USA
  // stars smeared on the nose top. A sliver at a crest's edge costs nothing.
  const coverAt = (x: number, y: number, bw: number, bh: number): number => {
    if (!seen) return 1;
    const x0m = ((x - bw / 2) / w) * seen.L;
    const x1m = ((x + bw / 2) / w) * seen.L;
    const y0m = ((y - bh / 2) / hgt) * seen.H;
    const y1m = ((y + bh / 2) / hgt) * seen.H;
    const keep = foldKeep(foldCover(seen, x0m, x1m, y0m, y1m), offLayerShare(seen.flank, seen.L, seen.H, x0m, x1m, y0m, y1m));
    return flankCover(seen, x0m, x1m, y0m, y1m) * (onWheel(x, y, bw, bh) ? 0.25 : 1) * keep;
  };
  // (polish r4: the share of a box over an up-facing shoulder alone)
  const foldAt = (x: number, y: number, bw: number, bh: number): number =>
    seen ? foldCover(seen, ((x - bw / 2) / w) * seen.L, ((x + bw / 2) / w) * seen.L, ((y - bh / 2) / hgt) * seen.H, ((y + bh / 2) / hgt) * seen.H) : 0;
  // (polish r4: the share of a box a side mirror hides from the
  // three-quarter views: BodyFit.mirrors)
  const mirrorAt = (x: number, y: number, bw: number, bh: number): number =>
    mapped && 'mirrors' in fit && fit.mirrors ? mirrorShare(fit.mirrors, ((x - bw / 2) / w) * mapped.L, ((x + bw / 2) / w) * mapped.L, ((y - bh / 2) / hgt) * mapped.H, ((y + bh / 2) / hgt) * mapped.H) : 0;
  // M154 polish r2: a STAR (a canton's, a constellation's, a lone one) is
  // seen only where nothing in its footprint is a surface the side painting
  // is drawn across (the smear map: a hood, a steep hood plane, a nose): a
  // star there tore into a white slash across the monster truck's and the
  // sports car's hoods. Crests keep coverAt (they are fitted to the shoulder
  // at the belt and were judged there).
  const starCover = (x: number, y: number, bw: number, bh: number): number => {
    const c = coverAt(x, y, bw, bh);
    if (!seen || c <= 0) return c;
    const s = smearCover(seen, ((x - bw / 2) / w) * seen.L, ((x + bw / 2) / w) * seen.L, ((y - bh / 2) / hgt) * seen.H, ((y + bh / 2) / hgt) * seen.H);
    return s <= SMEAR_FREE ? c : 0;
  };
  // M153: a GROUP (a canton, a constellation) moves as ONE: every member
  // keeps its place in the group and the group slides to where most of it
  // is on metal. Searching star by star scattered the USA's grid into a
  // curve and strung Australia's stars along the fender crown (re-review).
  // Canvas boxes already taken by a canton (the Union Jack): later stars and
  // crests keep off them (round 5: a Southern Cross star was laid over the
  // Jack's corner once the Jack moved off the coupe's gill slit).
  const occupied: { x0: number; x1: number; y0: number; y1: number }[] = [];
  const overlapAt = (x: number, y: number, bw: number, bh: number): number => {
    let o = 0;
    for (const r of occupied) {
      const ix = Math.max(0, Math.min(x + bw / 2, r.x1) - Math.max(x - bw / 2, r.x0));
      const iy = Math.max(0, Math.min(y + bh / 2, r.y1) - Math.max(y - bh / 2, r.y0));
      o += (ix * iy) / Math.max(1e-6, bw * bh);
    }
    return Math.min(1, o);
  };
  const placeGroup = (
    boxes: { x: number; y: number; w: number; h: number }[],
    reachBack: number,
    reachFwd: number,
    dys: number[],
  ): { dx: number; dy: number } => {
    if (!mapped) return { dx: 0, dy: 0 };
    let best = -Infinity;
    let out = { dx: 0, dy: 0 };
    for (let d = -reachFwd; d <= reachBack + 1e-9; d += 0.01) {
      for (const dyf of dys) {
        const dx = d * w;
        const dy = dyf * hgt;
        let cov = 0;
        let over = 0;
        for (const b of boxes) {
          cov += starCover(b.x + dx, b.y + dy, b.w, b.h);
          over += overlapAt(b.x + dx, b.y + dy, b.w, b.h);
        }
        const score = 2 * (cov / boxes.length) - 4 * over - 1.0 * Math.abs(d) - Math.abs(dyf);
        if (score > best) {
          best = score;
          out = { dx, dy };
        }
      }
    }
    return out;
  };
  const drawStar = (color: number, x: number, y: number, starH: number, points = 5): void => {
    const hb = starH / 0.22;
    blitSquare(g, w, hgt, fit, x, y, hb, (bg, size) => {
      bg.fillStyle = css(color);
      starPath(bg, size / 2, size / 2, 0.11 * size, points);
      bg.fill();
    });
  };

  // M154 polish r2: a crest's smallest READABLE size, in metres of the body
  // (Uruguay's sun on the Formula shrank to a 4-5 px speck, Japan's disc to
  // a dot), and the clear paint kept beside it along the car (the sports
  // car's side intake pressed against Japan's disc and Brazil's diamond).
  const pxPerM = mapped ? hgt / mapped.H : 0;
  const ring = CREST_RING * pxPerM * toX;
  const blit = (
    e: FlagEmblem,
    cx: number,
    cy: number,
    crestH: number,
    reach = 0.2,
    backing?: number,
    avoidMirror = false,
    ks: readonly number[] = CREST_KS,
    /** (polish r4: placements whose box stays inside this canvas rect) */
    bounds?: { x0: number; x1: number; y0: number; y1: number },
  ): void => {
    let bx = cx;
    let by = cy;
    let bh = crestH;
    if (mapped) {
      const cover0 = e.kind === 'star' ? starCover : coverAt;
      // (polish r4: a lone canton emblem keeps off the paint a mirror hides)
      const cover = avoidMirror ? (x: number, y: number, bw: number, bh2: number): number => cover0(x, y, bw, bh2) * (1 - mirrorAt(x, y, bw, bh2)) : cover0;
      const minH = Math.min(crestH, CREST_MIN_M * pxPerM);
      const search = (
        reachB: number,
        ks: readonly number[],
        inside = false,
        dys: readonly number[] = [0, -0.035, 0.035, 0.07, -0.07],
        /** (polish r4: only placements this much seen, crest and centre) */
        need = 0,
      ): { score: number; seen: number; core: number } => {
        let best = -Infinity;
        let seenBest = 0;
        let coreBest = 0;
        for (const k of ks) {
          const hh = crestH * k;
          if (hh < minH - 1e-6) continue;
          const ww = (hh * (w / hgt)) / fit.aspect;
          for (let dx = -reachB; dx <= reachB + 1e-9; dx += 0.01) {
            if (inside && (cx + dx * w - ww / 2 < 0 || cx + dx * w + ww / 2 > w)) continue;
            for (const dy of dys) {
              const x = cx + dx * w;
              const y = cy + dy * hgt;
              if (bounds && (x - ww / 2 < bounds.x0 || x + ww / 2 > bounds.x1 || y - hh / 2 < bounds.y0 || y + hh / 2 > bounds.y1)) continue;
              // Round 4: the crest's CENTRE must be clear. A thin slit or a door
              // handle costs the average little, so the bike's vent slots ran
              // through Serbia's and Slovakia's shields (Portugal's sphere read
              // as a smiley) and the pickup's handle sat in Japan's disc.
              const core = cover(x, y, ww * 0.72, hh * 0.72);
              const all = cover(x, y, ww, hh);
              if (
                need > 0 &&
                (all < need ||
                  core < need ||
                  foldAt(x, y, ww, hh) > CREST_COVER_FOLD ||
                  cover(x, y - CREST_COVER_TOP * hh * 0.5, ww * 0.6, hh * (1 + CREST_COVER_TOP)) < need)
              )
                continue;
              // (polish r2: and paint either side of it along the car, over
              // its middle rows: an intake's or an arch's edge pressed
              // against it read as the crest being swallowed)
              const side = cover(x, y, ww + 2 * ring, hh * 0.5);
              const score =
                2 * all + 3 * (core - 1) + 1.5 * (side - 1) - 4 * overlapAt(x, y, ww, hh) - 1.2 * Math.abs(dx) - Math.abs(dy) - 0.6 * (1 - k);
              if (score > best) {
                best = score;
                seenBest = all;
                coreBest = core;
                bx = x;
                by = y;
                bh = hh;
              }
            }
          }
        }
        return { score: best, seen: seenBest, core: coreBest };
      };
      const near = search(reach, ks);
      // (none at a readable size near its place: the nearest bigger clear
      // patch along the body, rather than a speck)
      if (near.seen < CREST_SEEN) {
        const keep = { bx, by, bh };
        const far = search(Math.max(reach, 0.45), ks, true);
        if (far.seen < near.seen + 0.1) ({ bx, by, bh } = keep);
      }
      // M154 polish r4: on the Formula a centred crest kept whole on its
      // short sidepod wall came out at three quarters of the live build's
      // size (Japan's disc, Brazil's diamond; live's ran over the sidepod's
      // top and folded). The tall flank of the engine cover behind the
      // cockpit holds it whole at the live size: it goes up there when the
      // band near its place cannot.
      // (a horizontal flag's: a vertical flag's crest keeps to its own band)
      if (horizontal && crestOnCover(fit, bh, crestH)) {
        const keep = { bx, by, bh };
        let found = false;
        for (const k of CREST_COVER_KS) {
          const r = search(CREST_COVER_REACH, [k], true, CREST_COVER_DYS, CREST_WHOLE);
          if (r.score > -Infinity) {
            found = true;
            break;
          }
        }
        if (!found) ({ bx, by, bh } = keep);
        // (polish r5: a crest on the cover's flank faces sideways, and the
        // race camera behind the car saw a sliver of it: the plan-view decal
        // lays the same crest whole on each sidepod top, wrapTopDecal)
        else if (extras) extras.coverCrests.push({ e, h: bh / pxPerM });
      }
    } else {
      bx = clearOfArches(cx, cy, (crestH * (w / hgt)) / fit.aspect, crestH);
    }
    const hb = bh / badgeExtent(e);
    blitSquare(g, w, hgt, fit, bx, by - (badgeCentreY(e) - 0.5) * hb, hb, (bg, size) => {
      if (backing !== undefined) {
        // (a canton's own colour round its emblem when the emblem leaves the
        // canton for a readable patch: Uruguay's sun on its white)
        bg.fillStyle = css(backing);
        bg.beginPath();
        bg.arc(size / 2, size / 2, 0.5 * size * badgeExtent(e) * 1.3, 0, Math.PI * 2);
        bg.fill();
      }
      drawEmblem(bg, e, size, size);
    });
  };

  // Badge pass: crests drawn square, placed ROUND. Centred crests anchor on
  // the door area (vertical flags: the flag's own centre, so Portugal's
  // sphere sits on its green/red seam); positioned stars (Australia, Panama,
  // DR Congo) keep their place in the field.
  const bandsN = spec.colors.length;
  // Crest height as a share of the metal band: a crest that must fit one of
  // three or more bands stays small (Iran, Egypt); a single field's crest or
  // a vertical flag's (Japan, Brazil, Canada, Senegal) is BIG, as it was
  // before the band rule shrank Canada's leaf to a badge (re-review).
  // M153 round 2: a little smaller than the first refit (0.8 / 0.6 / 0.82),
  // which pressed Japan's disc, Korea's taeguk and Mexico's seal against the
  // window trim and over the door handle.
  const centredShare = !horizontal ? 0.64 : bandsN >= 3 ? 0.34 : bandsN === 2 ? 0.52 : 0.7;
  const defaultX = spec.pattern === 'v' ? 0.5 : doorMid;
  for (const e of spec.emblems ?? []) {
    if (e.kind === 'canton') {
      // M153: the canton as a block at the hoist, as deep as the stripes it
      // covers (Greece 5 of 9, the USA 4 of 7, else half the band), its
      // stars in a tidy grid drawn round in metres.
      // Round 2: it is a FIELD, like a stripe: it sits in the hoist
      // corner (the nose) at the flag's own length share, and a vent may cut
      // it as it cuts the stripes. Searched for metal like a crest it slid
      // behind the front arch onto the coupe's gill slit and Chile's blue
      // shrank to a badge (white over red from the front: Poland). Only the
      // Union Jack keeps its 2:1 (its diagonals must run corner to corner).
      const n = spec.colors.length;
      const depth = e.cross !== undefined ? 5 / 9 : n >= 5 ? Math.round(0.54 * n) / n : 0.5;
      const ch = depth * fieldH;
      const cw = e.union ? ch * toX * 2 : (e.cross !== undefined ? 0.37 : 0.4) * w;
      const yy = atY(0);
      // The hoist is where the band first meets PAINT: over the Formula's
      // low, thin nose the band's top rows are air, and a canton anchored at
      // the very tip vanished (Australia's Union Jack).
      let x0 = 0;
      if (mapped) {
        for (let c = 0; c < 0.35 * w; c += w / 96) {
          const xa = (c / w) * mapped.L;
          const xb = ((c + cw / 4) / w) * mapped.L;
          if (flankCover(mapped, xa, xb, top * mapped.H, bot * mapped.H) >= 0.3 && flankCover(mapped, xa, xb, (yy / hgt) * mapped.H, ((yy + ch) / hgt) * mapped.H) >= 0.25) {
            x0 = c;
            break;
          }
        }
      }
      if (e.union) {
        // Round 3: on a short nose the 2:1 Union Jack landed over the front
        // arch (a sliver over the sports car's tyre); it slides back to the
        // first place it can be seen whole.
        // Round 4: scored like a crest, its centre clear (the first stretch
        // that was 60% metal put the coupe's gill slit through its corner).
        // Not on a ride without a cabin: the Formula's Jack sat right at the
        // nose and moved mid-sidepod among the Southern Cross (round 5).
        if (mapped && 'cabin' in fit && fit.cabin && !fit.cockpit) {
          let best = -Infinity;
          let bx = x0;
          for (let c = x0; c < x0 + 0.45 * w; c += w / 96) {
            const cov = coverAt(c + cw / 2, yy + ch / 2, cw, ch);
            const core = coverAt(c + cw / 2, yy + ch / 2, cw * 0.8, ch * 0.8);
            const score = 2 * cov + 3 * (core - 1) - (0.8 * (c - x0)) / w;
            if (score > best) {
              best = score;
              bx = c;
            }
          }
          x0 = bx;
        } else if (mapped) {
          // (without a cabin: the first place it can be seen, round 3's rule)
          for (let c = x0; c < x0 + 0.3 * w; c += w / 96) {
            if (coverAt(c + cw / 2, yy + ch / 2, cw, ch) >= 0.6) {
              x0 = c;
              break;
            }
          }
        }
        // painted into a 2:1 scratch canvas so its diagonals run corner to
        // corner of a box that is 2:1 in metres on the car
        const uc = document.createElement('canvas');
        uc.width = 400;
        uc.height = 200;
        unionJack(uc.getContext('2d')!, 0, 0, 400, 200);
        g.drawImage(uc, x0, yy, cw, ch);
        occupied.push({ x0, x1: x0 + cw, y0: yy, y1: yy + ch });
        continue;
      }
      g.fillStyle = css(e.color);
      g.fillRect(x0, yy, cw, ch);
      // M154 polish r3: ahead of the windscreen the canton reaches on up the
      // painting: a raised hood (the pickup's intake) stands above the band
      // and wore the top stripe there, a red box on the blue hood.
      if ('cabin' in fit && fit.cabin && 'L' in fit && x0 < 0.05 * w) {
        const xc = Math.min(x0 + cw, (fit.cabin.x0 / fit.L) * w);
        if (xc > x0) g.fillRect(x0, 0, xc - x0, yy + 1);
      }
      // Round 3: what is IN the canton (a star, the sun, the cross's upright,
      // the star grid) goes on the stretch of it that can be seen: at the
      // nose the front wheel runs through the block, and Chile's star, the
      // Uruguayan sun and the Greek cross sat behind the tyre.
      // M154 polish: and at the HEIGHT of it that can be seen: over the
      // Formula's low nose the canton's middle rows lie on the nose top
      // (the stars smeared into streaks there); they take the strip of the
      // canton the side shows whole (cantonStarStrip).
      let cx0 = x0;
      let cx1 = x0 + cw;
      let sy0 = yy;
      let sy1 = yy + ch;
      const ns = e.stars ?? 0;
      const sc = e.starColor ?? 0xffffff;
      // (two rows at least on a ride with a cabin: one long row read as a
      // starry racing stripe; the Formula's low canton keeps one, or its
      // top row was sliced by the body's edge, round 4)
      const minRows = ns >= 6 && 'cabin' in fit && fit.cabin && !fit.cockpit ? 2 : 1;
      let layout: { pts: { x: number; y: number }[]; h: number } | null = null;
      // (the canton block as drawn, for the plan-view decal)
      let drawn = { x1: x0 + cw, y1: yy + ch };
      let liveStarH: number | undefined;
      if (mapped) {
        // M154 polish r3: the stars go on the stretch of the canton where
        // they come out biggest with EVERY star clear of a smeared surface
        // (bestCantonLayout; the full-height stretch, the live layout, wins
        // a tie); when none is, round 2's rule: the strip whose columns keep
        // off the smear, else the old one, smear and all.
        // (polish r5: on the CANTON_LIVE_RIDES a stretch ends at a panel gap)
        const strict = CANTON_LIVE_RIDES.has(mapped.vehicle) && e.cross === undefined && ns > 1;
        const seams = mapped.seams ?? [];
        const stripCover = strict
          ? (x: number, y: number, bw: number, bh: number): number =>
              starOnSeam(seams, (x / w) * mapped.L, (y / hgt) * mapped.H, (bw / w) * mapped.L, (bh / hgt) * mapped.H, 0) ? 0 : coverAt(x, y, bw, bh)
          : coverAt;
        const strips = cantonStarStrips(stripCover, x0, x0 + cw, yy, yy + ch, w / 96);
        // M154 polish r5: on the Formula the plan-view decal lays a plate of
        // the canton's colour over the tops it smears across (the nose, the
        // sidepods by the cockpit: wrapTopDecal), so a star whose smear lands
        // under a plate is hidden there and may be as big as live's
        const plates = mapped?.plan && topDecalRide(mapped) && e.cross === undefined && ns > 1
          ? cantonTopBoxes(mapped.plan, mapped.L, mapped.W, { x0: (x0 / w) * mapped.L, x1: ((x0 + cw) / w) * mapped.L, y0: (yy / hgt) * mapped.H, y1: ((yy + ch) / hgt) * mapped.H })
          : null;
        const plated = (x: number, bw: number): boolean => {
          if (!plates || !mapped) return false;
          const xa = ((x - bw / 2) / w) * mapped.L;
          const xb = ((x + bw / 2) / w) * mapped.L;
          return [plates.centre, plates.side].some((b) => !!b && xa >= b.x0 && xb <= b.x1);
        };
        // (polish r5: the CANTON_LIVE_RIDES keep every star whole and off the
        // panel gaps)
        const judge = (x: number, y: number, h: number): number => {
          const k = e.sun ? 1.25 : 1;
          const c = coverAt(x, y, k * h * toX, k * h);
          if (strict && c > 0) {
            const hm = (k * h) / pxPerM;
            if (c < CANTON_LIVE_SEEN || starOnSeam(seams, (x / w) * mapped.L, (y / hgt) * mapped.H, hm, hm)) return -1;
          }
          return c > 0 && starCover(x, y, k * h * toX, k * h) <= 0 && !plated(x, k * h * toX) ? -1 : c;
        };
        let best = e.cross === undefined && ns >= 1 ? bestCantonLayout(strips, ns, toX, minRows, judge, 0.62, strict) : null;
        const old = cantonStarStrip(starCover, x0, x0 + cw, yy, yy + ch, w / 96);
        // (never smaller than round 2's layout, whose stars keep off the smear)
        if (best && old) {
          const L = cantonStarLayout(old, ns, toX, minRows);
          if (L.h > best.h * 1.02) best = null;
        }
        // M154 polish r4: where the stars that keep off the smear come out far
        // smaller than the live build's (its grid over the canton's longest
        // visible run at full height: the monster truck's top row tore over
        // the hood, but the clear grid was half its size), the canton runs
        // on back along the flank until a clear grid reaches the live size
        // (cantonGrowth).
        // (the live build's run ignores the panel gaps, as live did)
        const liveStrips = strict ? cantonStarStrips(coverAt, x0, x0 + cw, yy, yy + ch, w / 96) : strips;
        const liveRun = liveStrips[0] && liveStrips[0].y0 === yy && liveStrips[0].y1 === yy + ch ? liveStrips[0] : null;
        if (e.cross === undefined && ns > 1 && liveRun) {
          const liveH = cantonStarLayout(liveRun, ns, toX, minRows).h;
          liveStarH = liveH / pxPerM;
          // (the stars on the canton's own length at its far end: at the nose
          // the hood's smear and the arch take its front)
          // (one stripe deeper: the field's band height over its colours)
          const stripe = fieldH / Math.max(1, n);
          // (a CANTON_LIVE_RIDE's canton stops short of the first panel gap
          // behind it)
          const endM = ((x0 + cw) / w) * mapped.L;
          const gap = seams.filter((sm) => sm.x > endM && sm.y1 > (yy / hgt) * mapped.H && sm.y0 < ((yy + ch) / hgt) * mapped.H).sort((p, q) => p.x - q.x)[0];
          const maxGrow = gap ? Math.max(1, ((gap.x - 0.01) / mapped.L * w - x0) / cw) : 1.6;
          const grown = cantonGrowth(liveH, best?.h ?? 0, (st) =>
            bestCantonLayout(
              cantonStarStrips(stripCover, x0 + cw * (st.grow - 1), x0 + cw * st.grow, yy, yy + ch + st.deep * stripe, w / 96),
              ns,
              toX,
              minRows,
              judge,
              st.fill,
              strict,
            ),
            strict ? CANTON_LIVE_TO[mapped.vehicle]! - 0.01 : CANTON_GROW_BELOW,
            strict ? CANTON_LIVE_TO[mapped.vehicle]! : CANTON_GROW_TO,
            strict ? liveCantonSteps(maxGrow) : CANTON_GROW_STEPS,
          );
          if (grown) {
            best = grown.layout;
            g.fillStyle = css(e.color);
            g.fillRect(x0, yy, cw * grown.step.grow, ch + grown.step.deep * stripe);
            drawn = { x1: x0 + cw * grown.step.grow, y1: yy + ch + grown.step.deep * stripe };
          }
        }
        const strip = best?.strip ?? old ?? cantonStarStrip(coverAt, x0, x0 + cw, yy, yy + ch, w / 96);
        if (strip) ({ x0: cx0, x1: cx1, y0: sy0, y1: sy1 } = strip);
        if (best) layout = best;
      }
      const cmid = (cx0 + cx1) / 2;
      layout ??= cantonStarLayout({ x0: cx0, x1: cx1, y0: sy0, y1: sy1 }, ns, toX, minRows);
      if (extras && mapped && e.cross === undefined && ns > 1) {
        extras.cantons.push({
          x0: (x0 / w) * mapped.L,
          x1: (drawn.x1 / w) * mapped.L,
          y0: (yy / hgt) * mapped.H,
          y1: (drawn.y1 / hgt) * mapped.H,
          color: e.color,
          starColor: sc,
          stars: ns,
          starH: layout.h / pxPerM,
          liveH: liveStarH,
        });
      }
      if (e.cross !== undefined) {
        g.fillStyle = css(e.cross);
        g.fillRect(x0, yy + (2 / 9) * fieldH, cw, fieldH / 9);
        const uw = (fieldH / 9) * toX;
        g.fillRect(cmid - uw / 2, yy, uw, ch);
        continue;
      }
      if (ns === 1) {
        const sh = layout.h;
        const emb: FlagEmblem = e.sun ? { kind: 'sun', color: sc } : { kind: 'star', color: sc, points: 5 };
        if (mapped && sh / pxPerM < CANTON_EMBLEM_MIN_M) {
          // M154 polish r2: too small in the stretch of canton the side
          // shows (the Formula's thin nose): the emblem goes to a readable
          // clear patch near the canton, on a disc of the canton's colour.
          blit(emb, x0 + cw / 2, yy + ch / 2, Math.max(sh, Math.min(0.6 * ch, CANTON_EMBLEM_MIN_M * 1.6 * pxPerM)), 0.3, e.color);
          continue;
        }
        const at = layout.pts[0]!;
        // M154 polish r4: ...and when the sun comes out a dot (the rebuilt
        // sports car's by its arch), squeezed by its canton's visible run
        // (today's sports car's) or behind a side mirror from the angled views
        // (the rebuilt pickup's), it goes to the nearest clear patch no mirror
        // hides, at the live build's size or bigger: never smaller.
        if (mapped && e.sun) {
          const k = 1.25;
          if (cantonEmblemMoves(sh / pxPerM, mirrorAt(at.x, at.y, k * sh * toX, k * sh), sh / ch)) {
            const target = Math.max(sh, CANTON_EMBLEM_LIVE_M * pxPerM);
            // (the sun is drawn a tenth over its cell, as live)
            // (inside the canton's own field: over a stripe its disc of the
            // canton's colour cut a notch out of the band)
            blit(emb, at.x, at.y, target * 1.1, 0.3, undefined, true, CANTON_MOVE_KS, { x0, x1: x0 + cw, y0: yy, y1: yy + ch });
            continue;
          }
        }
        if (e.sun) {
          // Uruguay's Sun of May (a plain star read as no flag at all)
          const sun: FlagEmblem = { kind: 'sun', color: sc };
          const hb = (sh * 1.1) / badgeExtent(sun);
          blitSquare(g, w, hgt, fit, at.x, at.y, hb, (bg, size) => drawEmblem(bg, sun, size, size));
        } else drawStar(sc, at.x, at.y, sh);
      } else if (ns > 1) {
        for (const p of layout.pts) drawStar(sc, p.x, p.y, layout.h);
      }
      continue;
    }
    if (e.kind === 'stars') {
      // a constellation keeps its shape: one slide for the whole group
      const items = e.pts.map(([x, y, r, pts]) => ({ x: x * w, y: atY(y), h: 2 * r * 1.6 * fieldH, pts: pts ?? 5 }));
      const boxes = items.map((it) => ({ x: it.x, y: it.y, w: it.h * toX, h: it.h }));
      const { dx, dy } = placeGroup(boxes, 0.12, 0.12, [0, -0.03, 0.03]);
      for (const it of items) drawStar(e.color, it.x + dx, it.y + dy, it.h, it.pts);
      continue;
    }
    if (isStretchEmblem(e)) continue;
    let ax = defaultX;
    let ay = 0.5;
    let positioned = false;
    let centred: FlagEmblem = e;
    if (e.kind === 'disc') {
      if (e.x !== undefined) ax = e.x;
      centred = { ...e, x: 0.5 };
    } else if (e.kind === 'star') {
      positioned = e.x !== undefined || e.y !== undefined;
      if (e.x !== undefined) ax = e.x;
      if (e.y !== undefined) ay = e.y;
      centred = { ...e, x: 0.5, y: 0.5, points: e.points };
    } else if (e.kind === 'wyvern') {
      // (a little low in the band: centred, the raised wing's tips met the
      // line under the coupe's side glass and were cut flat, round 5)
      blit(e, defaultX * w, atY(0.56), 0.86 * fieldH, 0.2);
      continue;
    } else if (e.kind === 'shield') {
      ax = e.x ?? 0.36;
      ay = e.y ?? 0.5;
      const sh = 0.62 * fieldH;
      blit(e, ax * w, atY(ay), sh, 0.14);
      continue;
    }
    const own = e.kind === 'disc' && e.h !== undefined ? Math.min(e.h, 0.62) : e.kind === 'checker' ? e.h : undefined;
    const share = own ?? (positioned ? 0.34 : centredShare); // crest height as a share of the band
    // A vertical flag's crest stays inside its own band; a positioned star
    // near its corner; a single field's crest may travel along the door.
    blit(centred, ax * w, atY(ay), share * fieldH, positioned ? 0.15 : !horizontal ? 0.12 : 0.2);
  }
  return c;
}

// ---------------------------------------------------------------------------
// M154 polish r5 — the Formula's PLAN-VIEW decal over a flag wrap.
// ---------------------------------------------------------------------------

/** What wrapCanvas reports for the plan-view decal: the crests it put whole
 *  on the engine cover's flank (metres tall), and every star canton's block
 *  (side-view metres: x from the nose, y down from the roof line). */
export interface WrapExtras {
  coverCrests: { e: FlagEmblem; h: number }[];
  cantons: { x0: number; x1: number; y0: number; y1: number; color: number; starColor: number; stars: number; starH: number; liveH?: number }[];
}

export const newWrapExtras = (): WrapExtras => ({ coverCrests: [], cantons: [] });

/** The rides whose flag wraps get the plan-view decal: an open cockpit, no
 *  cabin, a measured plan (the Formula). */
export function topDecalRide(fit: LiveryFit | LiveryMeasure): boolean {
  return 'cockpit' in fit && !!fit.cockpit && !fit.cabin && !!fit.plan?.height && !!fit.plan.ny;
}

/** A sidepod top cell faces up at least this much (the decal shows from
 *  n.y 0.6 up, cross-faded to 0.655). */
export const POD_TOP_NY = 0.68;
/** ...and lies within this share of the crest's width of the crest centre's
 *  height (one smooth surface, sloping at most so much; not a step onto the
 *  engine cover or down a wall). */
export const POD_TOP_RISE = 0.45;
/** The pod-top crest never shrinks below this share of the flank crest. */
export const POD_CREST_MIN = 0.6;
/** ...and is drawn this much longer along the car than across (the race
 *  camera sees the pod top foreshortened; the pod-top numbers' stretch). */
export const POD_CREST_STRETCH = 1.7;
/** The elevation of the race camera the pod-top crest is placed for (rad):
 *  a pod top that slopes down toward the tail faces it more squarely. */
export const POD_CHASE_ELEV = 0.45;
/** A top cell a canton's stars go on faces up at least this much. */
export const CANTON_TOP_NY = 0.64;

/** (pure) How squarely a top cell faces the race camera behind and above the
 *  car: its slope toward the tail from the plan map's heights (metres DOWN
 *  from the roof line per metre along), against the camera's direction. */
export function chaseFacing(plan: FlankMap, L: number, c: number, r: number): number {
  const { cols, cov } = plan;
  const height = plan.height!;
  const k = (cc: number): number => r * cols + cc;
  const ok = (cc: number): boolean => cc >= 0 && cc < cols && cov[k(cc)] === 1 && height[k(cc)]! >= 0;
  let s = 0;
  if (ok(c - 1) && ok(c + 1)) s = (height[k(c + 1)]! - height[k(c - 1)]!) / ((2 * L) / cols);
  return Math.max(0, (s * Math.cos(POD_CHASE_ELEV) + Math.sin(POD_CHASE_ELEV)) / Math.hypot(1, s));
}

/**
 * (pure; the tests pin it) The biggest disc, `dMax` metres across at most
 * (down to POD_CREST_MIN of it) and `stretch` times that along the car,
 * that lies wholly on one smooth up-facing
 * surface of the plan map on BOTH sides at once (side 0's spot mirrored
 * across the centre line): the Formula's sidepod tops. Of every size and
 * spot that holds, the one that shows the race camera the most (its area
 * times how squarely it faces the camera, chaseFacing), then the biggest,
 * then the middle one. `zm` counts metres across from the plan's row 0. Null
 * when there is none.
 */
export function podTopSpot(plan: FlankMap, L: number, W: number, dMax: number, stretch = 1): { x: number; zm: number; d: number } | null {
  const { cols, rows, cov } = plan;
  const height = plan.height;
  const ny = plan.ny;
  if (!height || !ny) return null;
  const dx = L / cols;
  const dz = W / rows;
  let best: { x: number; zm: number; d: number; score: number }[] = [];
  let bestScore = -Infinity;
  for (let d = dMax; d >= POD_CREST_MIN * dMax - 1e-9; d -= 0.02) {
    const rad = d / 2;
    const radX = rad * stretch;
    for (let xi = 0; xi < cols * 2; xi++) {
      const x = (xi + 1) * (dx / 2);
      for (let zi = 0; zi < rows; zi++) {
        const zm = (zi + 1) * (dz / 2);
        if (zm + rad > W / 2) break;
        const cc = Math.floor(x / dx);
        const rc = Math.floor(zm / dz);
        if (cc >= cols || rc >= rows) continue;
        const h0 = height[rc * cols + cc]!;
        if (!(h0 >= 0) || cov[rc * cols + cc] !== 1) continue;
        let good = true;
        let face = 0;
        let n = 0;
        const c0 = Math.floor((x - radX) / dx);
        const c1 = Math.floor((x + radX) / dx);
        const r0 = Math.floor((zm - rad) / dz);
        const r1 = Math.floor((zm + rad) / dz);
        for (let c = c0; c <= c1 && good; c++) {
          for (let r = r0; r <= r1 && good; r++) {
            // (the cells the disc reaches past a cell's half-width)
            const ex = Math.max(Math.abs((c + 0.5) * dx - x) - dx / 2, 0);
            const ez = Math.max(Math.abs((r + 0.5) * dz - zm) - dz / 2, 0);
            if (Math.hypot(ex / stretch, ez) > rad) continue;
            for (const rr of [r, rows - 1 - r]) {
              if (c < 0 || rr < 0 || c >= cols || rr >= rows) {
                good = false;
                break;
              }
              const k = rr * cols + c;
              if (cov[k] !== 1 || ny[k]! < POD_TOP_NY || !(height[k]! >= 0) || Math.abs(height[k]! - h0) > POD_TOP_RISE * d) {
                good = false;
                break;
              }
            }
            if (good) {
              face += chaseFacing(plan, L, c, r);
              n++;
            }
          }
        }
        if (!good || !n) continue;
        // (what the race camera sees of it: its area, squared up)
        const score = d * d * (face / n);
        if (score > bestScore) bestScore = score;
        if (score >= bestScore * 0.99) best.push({ x, zm, d, score });
      }
    }
  }
  best = best.filter((v) => v.score >= bestScore * 0.99);
  if (!best.length) return null;
  const dTop = Math.max(...best.map((v) => v.d));
  const big = best.filter((v) => v.d >= dTop - 1e-9);
  const xs = big.map((v) => v.x).sort((p, q) => p - q);
  const xMid = xs[Math.floor(xs.length / 2)]!;
  const at = big.filter((v) => Math.abs(v.x - xMid) < 1e-6).sort((p, q) => p.zm - q.zm);
  const pick = at[Math.floor(at.length / 2)]!;
  return { x: pick.x, zm: pick.zm, d: pick.d };
}

/**
 * (pure; the tests pin it) The stretches of the TOP surfaces that show a
 * canton's colour: cells facing up, along the canton's length, at a height
 * inside its block (the side painting lies across them in the canton's
 * colour). The biggest box of such cells round the centre line (the
 * Formula's nose: `half` = its half-width) and the biggest box on side 0
 * that holds on its mirror too (a sidepod top: rows r0..r1 from row 0),
 * each in metres along the car. Either may be null.
 */
export function cantonTopBoxes(
  plan: FlankMap,
  L: number,
  W: number,
  canton: { x0: number; x1: number; y0: number; y1: number },
): { centre: { x0: number; x1: number; half: number } | null; side: { x0: number; x1: number; z0: number; z1: number } | null } {
  const { cols, rows, cov } = plan;
  const height = plan.height;
  const ny = plan.ny;
  if (!height || !ny) return { centre: null, side: null };
  const dz = W / rows;
  const good = (c: number, r: number): boolean => {
    const x = ((c + 0.5) / cols) * L;
    if (x < canton.x0 + 0.02 || x > canton.x1 - 0.02) return false;
    const k = r * cols + c;
    return cov[k] === 1 && ny[k]! >= CANTON_TOP_NY && height[k]! >= canton.y0 + 0.01 && height[k]! <= canton.y1 - 0.01;
  };
  const best = (fits: (c: number) => boolean): [number, number] | null => {
    let out: [number, number] | null = null;
    let run0 = -1;
    for (let c = 0; c <= cols; c++) {
      const ok = c < cols && fits(c);
      if (ok && run0 < 0) run0 = c;
      if (!ok && run0 >= 0) {
        if (!out || c - run0 > out[1] - out[0]) out = [run0, c];
        run0 = -1;
      }
    }
    return out;
  };
  // round the centre line
  let centre: { x0: number; x1: number; half: number } | null = null;
  const mid0 = Math.floor(rows / 2) - 1;
  const mid1 = Math.ceil(rows / 2);
  for (let n = 1; n <= Math.floor(rows / 2); n++) {
    const run = best((c) => {
      for (let i = 0; i < n; i++) if (!good(c, mid0 - i) || !good(c, mid1 + i)) return false;
      return true;
    });
    if (!run) break;
    const box = { x0: (run[0] / cols) * L, x1: (run[1] / cols) * L, half: n * dz };
    if (!centre || (box.x1 - box.x0) * box.half > (centre.x1 - centre.x0) * centre.half) centre = box;
  }
  // off it, on both sides (rows on side 0, kept clear of the centre box)
  let side: { x0: number; x1: number; z0: number; z1: number } | null = null;
  const inner = centre ? Math.floor(rows / 2) - Math.round(centre.half / dz) - 1 : Math.floor(rows / 2) - 1;
  for (let r0 = 0; r0 <= inner; r0++) {
    for (let r1 = r0 + 1; r1 <= inner; r1++) {
      const run = best((c) => {
        for (let r = r0; r <= r1; r++) if (!good(c, r) || !good(c, rows - 1 - r)) return false;
        return true;
      });
      if (!run) continue;
      const box = { x0: (run[0] / cols) * L, x1: (run[1] / cols) * L, z0: r0 * dz, z1: (r1 + 1) * dz };
      if (!side || (box.x1 - box.x0) * (box.z1 - box.z0) > (side.x1 - side.x0) * (side.z1 - side.z0)) side = box;
    }
  }
  return { centre, side };
}

/** (pure; the tests pin it) A grid of `n` stars over a box `len` long and
 *  `wid` wide (metres): the rows across that make the stars biggest, and
 *  the star's height (`fill` of the smaller cell side). */
export function plateStarGrid(n: number, len: number, wid: number, fill = 0.8): { rows: number; cols: number; h: number } {
  let best = { rows: 1, cols: n, h: 0 };
  for (let rows = 1; rows <= Math.min(4, n); rows++) {
    const cols = Math.ceil(n / rows);
    const h = fill * Math.min(len / cols, wid / rows);
    if (h > best.h + 1e-9) best = { rows, cols, h };
  }
  return best;
}

/**
 * (pure; the tests pin it) How many of a canton's `stars` go on a stretch of
 * top `len` by `wid` metres, and how: from a third of the canton's count (at
 * least two) up to all of it, the count whose grid shows the most star
 * (count x height squared): a narrow nose wears one row of big stars rather
 * than two rows of small ones.
 */
export function topStarLayout(stars: number, len: number, wid: number): { n: number; rows: number; cols: number; h: number } {
  let best = { n: stars, ...plateStarGrid(stars, len, wid, TOP_STAR_FILL) };
  let score = best.n * best.h * best.h;
  for (let n = Math.max(2, Math.ceil(stars / 3)); n <= stars; n++) {
    const gr = plateStarGrid(n, len, wid, TOP_STAR_FILL);
    const sc = n * gr.h * gr.h;
    if (sc > score + 1e-12) {
      score = sc;
      best = { n, ...gr };
    }
  }
  return best;
}
/** A top star's height as a share of its grid cell. */
export const TOP_STAR_FILL = 0.95;

/**
 * The plan-view decal over a flag wrap (null when the wrap needs none):
 * only on a ride with an open cockpit and no cabin (the Formula), where the
 * side painting is smeared across every top surface. A crest wrapCanvas
 * moved onto the engine cover's flank (Japan's disc, Brazil's diamond) is
 * laid WHOLE on each sidepod top as well, upright for the race camera
 * behind the car; a star canton's stars go clean on the tops that show its
 * colour (the nose, the sidepods beside the cockpit), where the side
 * painting's stars smeared into streaks (live) or were kept off as specks.
 * Transparent elsewhere. `minY` = the lowest height (0..1 of the body) the
 * decal may land on, so nothing under the tops it is drawn for (the front
 * wing, the floor) picks it up.
 */
export function wrapTopDecal(fit: LiveryMeasure, extras: WrapExtras): { canvas: HTMLCanvasElement; minY: number } | null {
  if (!topDecalRide(fit) || !fit.plan?.height) return null;
  if (!extras.coverCrests.length && !extras.cantons.length) return null;
  const { L, H, W } = fit;
  const plan = fit.plan;
  const ph = fit.plan.height;
  const cw = 2048;
  const ch = Math.max(64, Math.round((cw * W) / L));
  const c = document.createElement('canvas');
  c.width = cw;
  c.height = ch;
  const g = c.getContext('2d')!;
  // one unit = one metre; canvas y runs from the body's far side (the
  // texture's v = the across coordinate, flipped)
  g.scale(cw / L, ch / W);
  let minY = 1;
  let drew = false;
  const atY = (zm: number): number => W - zm;
  /** the lowest top (0..1 of the height) under a box, less a margin */
  const lowest = (x0: number, x1: number, z0: number, z1: number): number => {
    let low = Infinity;
    for (let cc = 0; cc < plan.cols; cc++) {
      const x = ((cc + 0.5) / plan.cols) * L;
      if (x < x0 || x > x1) continue;
      for (let r = 0; r < plan.rows; r++) {
        const zm = ((r + 0.5) / plan.rows) * W;
        if (zm < z0 || zm > z1) continue;
        const hh = ph[r * plan.cols + cc]!;
        if (hh >= 0 && plan.cov[r * plan.cols + cc] === 1) low = Math.min(low, 1 - hh / H);
      }
    }
    return Number.isFinite(low) ? low - 0.08 : 1;
  };
  for (const crest of extras.coverCrests) {
    const spot = podTopSpot(plan, L, W, crest.h, POD_CREST_STRETCH);
    if (!spot) continue;
    const k = badgeExtent(crest.e);
    const sq = document.createElement('canvas');
    sq.width = sq.height = 512;
    const sg = sq.getContext('2d')!;
    sg.translate(0, 512 * (0.5 - badgeCentreY(crest.e)));
    drawEmblem(sg, crest.e, 512, 512);
    const s = spot.d / k;
    for (const zm of [spot.zm, W - spot.zm]) {
      g.save();
      g.translate(spot.x, atY(zm));
      // the emblem's top toward the nose (canvas x = 0): it reads upright
      // from the race camera behind the car
      g.rotate(-Math.PI / 2);
      // (drawn long along the car, as the pod-top numbers are: the race
      // camera looks along the pod top and sees it foreshortened)
      g.scale(1, POD_CREST_STRETCH);
      g.drawImage(sq, -s / 2, -s / 2, s, s);
      g.restore();
      const hx = (spot.d * POD_CREST_STRETCH) / 2;
      minY = Math.min(minY, lowest(spot.x - hx, spot.x + hx, zm - spot.d / 2, zm + spot.d / 2));
    }
    drew = true;
  }
  const star = (x: number, zm: number, h: number): void => {
    g.save();
    g.translate(x, atY(zm));
    g.rotate(-Math.PI / 2);
    starPath(g, 0, 0, h / 2, 5);
    g.fill();
    g.restore();
  };
  for (const ct of extras.cantons) {
    const { centre, side } = cantonTopBoxes(plan, L, W, ct);
    // the plates first: the canton's colour over the tops it already lies on
    // (it hides the side stars' smear there; wrapCanvas sized them for it)
    g.fillStyle = css(ct.color);
    if (centre) g.fillRect(centre.x0, atY(W / 2 + centre.half), centre.x1 - centre.x0, 2 * centre.half);
    if (side) for (const [z0, z1] of [[side.z0, side.z1], [W - side.z1, W - side.z0]] as const) g.fillRect(side.x0, atY(z1), side.x1 - side.x0, z1 - z0);
    g.fillStyle = css(ct.starColor);
    const lay = (x0: number, len: number, zc: number, wid: number): void => {
      const grid = topStarLayout(ct.stars, len, wid);
      const n = grid.n;
      const cellL = len / grid.cols;
      const cellW = wid / grid.rows;
      for (let i = 0; i < n; i++) {
        const col = i % grid.cols;
        const row = Math.floor(i / grid.cols);
        // (a short last row is centred)
        const inRow = row === grid.rows - 1 ? n - row * grid.cols : grid.cols;
        const xs = x0 + (len - inRow * cellL) / 2 + (col + 0.5) * cellL;
        star(xs, zc - wid / 2 + (row + 0.5) * cellW, grid.h);
      }
    };
    if (centre && centre.x1 - centre.x0 > 0.1) {
      const len = centre.x1 - centre.x0 - 0.04;
      const wid = 2 * centre.half - 0.02;
      lay(centre.x0 + 0.02, len, W / 2, wid);
      minY = Math.min(minY, lowest(centre.x0, centre.x1, W / 2 - centre.half, W / 2 + centre.half));
      drew = true;
    }
    if (side && side.x1 - side.x0 > 0.1) {
      const len = side.x1 - side.x0 - 0.04;
      const wid = side.z1 - side.z0 - 0.02;
      for (const zc of [(side.z0 + side.z1) / 2, W - (side.z0 + side.z1) / 2]) {
        lay(side.x0 + 0.02, len, zc, wid);
        minY = Math.min(minY, lowest(side.x0, side.x1, zc - wid / 2, zc + wid / 2));
      }
      drew = true;
    }
  }
  return drew ? { canvas: c, minY: Math.max(0, minY) } : null;
}

// ---------------------------------------------------------------------------
// Vehicle part classification (M127) — the ONE place a GLB mesh becomes
// "paint / glass / tyre / rim / trim / chrome / lamp". Matched on the node
// name + the GLB material name and CACHED on the mesh: GLTFLoader names a
// multi-material node's children "Body", "Body_1", "Body_2", so once the
// first material pass has replaced the GLB materials, the material NAME (the
// only thing that said "Body_1 is the wheel well" or "Greenhouse_1 is
// glass") is gone — a re-apply (the shop preview re-dresses the same model on
// every card) would have painted the windows. The first sight is remembered.
// ---------------------------------------------------------------------------

export type CarPart =
  | 'carbon'
  | 'gold'
  | 'chrome'
  | 'trim'
  | 'rim'
  | 'tyre'
  | 'glass'
  | 'head'
  | 'tail'
  | 'paint'
  // M154: opt-in finishes, chosen by an exact NAME PREFIX (see PREFIX_PARTS)
  | 'lens'
  | 'ledred'
  | 'ledcool'
  | 'amber'
  | 'charcoal'
  | 'glassclear'
  | 'brake'
  | 'satin';

/**
 * M154: the finishes the concept pack needs that the old keyword classes
 * could not express (the one shared tail-light material rendered coral, a
 * "charcoal" plate came out mirror chrome, a smoked red lens did not exist).
 * Matched on the object's or material's name PREFIX only, never a substring
 * ("lens" already appears inside Trim_dark_lensbed and Glass_taillens), and
 * checked first, so no part named before M154 changes class.
 */
export const PREFIX_PARTS: ReadonlyArray<readonly [string, CarPart]> = [
  ['Lens_', 'lens'], // smoked dark red tail-lamp lens
  ['LEDred_', 'ledred'], // deep red LED line / dot
  ['LEDcool_', 'ledcool'], // cool white LED (monster truck, bike)
  ['Amber_', 'amber'], // amber fog / marker
  ['Charcoal_', 'charcoal'], // satin charcoal plate, liner, bars
  ['GlassClear_', 'glassclear'], // see-through glass (windscreens, bike screen)
  ['Brake_', 'brake'], // red brake caliper on a car whose paint is not red
  // (M154 r5) a light satin steel plate that never mirrors the sky: the
  // pickup's skid plate rendered as a polished mirror (Chrome_) took each
  // city's sky colour, a flat pale card in the studio and dark at night
  ['Satin_', 'satin'],
];

export function carPartOf(o: THREE.Object3D): CarPart {
  const cached = o.userData.carPart as CarPart | undefined;
  if (cached) return cached;
  const m = o as THREE.Mesh;
  const mat = m.material as THREE.Material | undefined;
  const t = `${o.name} ${mat?.name ?? ''}`.toLowerCase();
  let part: CarPart | undefined;
  const matName = mat?.name ?? '';
  for (const [prefix, cls] of PREFIX_PARTS) {
    if (o.name.startsWith(prefix) || matName.startsWith(prefix)) {
      part = cls;
      break;
    }
  }
  // Order matters (specific before general). M50 fix: TRIM before RIM —
  // "trim" contains "rim", so mirror heads / grilles / the bike rider's suit
  // were silently rendering as polished rim metal.
  // M150: CARBON first — the rebuilt fleet names its aero "Carbon_splitter",
  // "Carbon_diffuser", and those words would otherwise route it to dark trim.
  if (part !== undefined) {
    // an M154 prefix class (above)
  } else if (t.includes('carbon')) part = 'carbon';
  // M151: gold anodised parts (the hyperbike's inverted fork tubes).
  else if (t.includes('gold')) part = 'gold';
  else if (t.includes('chrome') || t.includes('exhaust') || t.includes('pipe') || t.includes('metal'))
    part = 'chrome'; // before trim: a Chrome-material rollbar stays chrome
  else if (
    t.includes('trim') ||
    t.includes('bumper') ||
    t.includes('grille') ||
    t.includes('splitter') ||
    t.includes('skirt') ||
    t.includes('diffuser') ||
    t.includes('rollbar') ||
    t.includes('rack') ||
    t.includes('seat') ||
    t.includes('suit') ||
    t.includes('stripe')
  )
    part = 'trim';
  else if (t.includes('rim')) part = 'rim';
  else if (t.includes('wheel') || t.includes('tyre') || t.includes('tire')) part = 'tyre';
  else if (t.includes('glass') || t.includes('window') || t.includes('visor')) part = 'glass';
  else if (t.includes('headlight') || t.includes('headlamp')) part = 'head';
  else if (t.includes('taillight') || t.includes('taillamp')) part = 'tail';
  else part = 'paint'; // body + anything unlabelled (incl. the Helmet)
  o.userData.carPart = part;
  if (part === 'trim') o.userData.trimDark = isDarkTrim(t);
  return part as CarPart;
}

/**
 * M153: which trim parts may wear a kit's SHORTS colour. The M144 rule is
 * "shorts = the bumpers, the sills, the pickup's bed": the plain Trim group.
 * Everything else that is trim stays dark under any look, because on the
 * rebuilt fleet it is a hole or a detail, not a panel:
 *   - Trim_well: the inside of every cavity cut into the shell (wheel wells,
 *     the sports car's side scoop, lamp pockets): a white scoop pocket read as
 *     a blank plate on a Juventus car (measured on the lab sheets);
 *   - the glasshouse frame and pillars (the pickup's cab turned white/blue);
 *   - the rider's suit and the seats (the riders are all black, owner rule);
 *   - the dark and grey accent groups: grilles, mesh, vents, lamp beds;
 *   - calipers and mirror bases.
 * True = always the stock dark trim.
 */
function isDarkTrim(t: string): boolean {
  return /trim_well|trim_dark|trim_grey|trim_gloss|greenhouse|suit|seat|grille|mesh|vent|intake|scoop|caliper|mirror/.test(t);
}

/** M153: true when a trim part keeps the stock dark trim under every look. */
export function trimIsDark(o: THREE.Object3D): boolean {
  if (carPartOf(o) !== 'trim') return false;
  return o.userData.trimDark === true;
}

// ---------------------------------------------------------------------------
// Body measurement + UV projection (M127).
// ---------------------------------------------------------------------------

/** A measured painted body: the fit numbers plus the root-local box the UVs
 *  are normalised against. M144: it is also a full BodyFit (metres, cabin
 *  span, wheel hubs) — what the three-view painters draw against. */
export interface LiveryMeasure extends LiveryFit, BodyFit {
  /** Root-local AABB of the painted body (helmet + small bolt-ons excluded). */
  box: THREE.Box3;
  /** The root-local axis the painted length runs along. */
  lengthAxis: 'x' | 'z';
}

const CORNERS = [0, 1, 2, 3, 4, 5, 6, 7].map(
  (i) => new THREE.Vector3(i & 1 ? 1 : 0, i & 2 ? 1 : 0, i & 4 ? 1 : 0),
);

/** A mesh's AABB in the root's local space (exact corners, not a rotated box). */
function localBox(mesh: THREE.Mesh, inv: THREE.Matrix4, out: THREE.Box3): THREE.Box3 {
  const geo = mesh.geometry as THREE.BufferGeometry;
  if (!geo.boundingBox) geo.computeBoundingBox();
  const bb = geo.boundingBox!;
  const rel = new THREE.Matrix4().copy(inv).multiply(mesh.matrixWorld);
  out.makeEmpty();
  const p = new THREE.Vector3();
  for (const k of CORNERS) {
    p.set(
      k.x ? bb.max.x : bb.min.x,
      k.y ? bb.max.y : bb.min.y,
      k.z ? bb.max.z : bb.min.z,
    ).applyMatrix4(rel);
    out.expandByPoint(p);
  }
  return out;
}

/**
 * Find the PAINTED BODY of a fitted vehicle: the union of its paint meshes,
 * ignoring the driver's helmet and bolt-ons under 15% of the biggest paint
 * part's volume (spoilers, scoops, the Formula's wings, the bike's fenders —
 * they still get painted, sampling the nearest edge of the livery). The belt
 * is the lowest glass (visors excluded); a body with no glass (the Formula)
 * takes a default. Deterministic per model, so every clone measures alike.
 */
export function measureLivery(root: THREE.Object3D, vehicleId = ''): LiveryMeasure {
  // M153: a fitted model's measure never changes (the flank map rasterises
  // every paint triangle), so it is remembered on the root.
  const memo = root.userData.liveryMeasure as LiveryMeasure | undefined;
  if (memo && memo.vehicle === vehicleId) return memo;
  root.updateMatrixWorld(true);
  const inv = root.matrixWorld.clone().invert();
  const scratch = new THREE.Box3();
  const size = new THREE.Vector3();
  const paints: { box: THREE.Box3; vol: number }[] = [];
  const glass = new THREE.Box3();
  const tyres: THREE.Box3[] = [];
  const helmets: THREE.Box3[] = [];
  const scoops: THREE.Box3[] = [];
  const mirrorBoxes: THREE.Box3[] = [];
  const paintMeshes: THREE.Mesh[] = [];
  const glassMeshes: THREE.Mesh[] = [];
  const otherMeshes: THREE.Mesh[] = [];
  let glassMin = Infinity;
  root.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh || !m.geometry) return;
    const part = carPartOf(m);
    const name = o.name.toLowerCase();
    if (name.includes('scoop')) scoops.push(localBox(m, inv, scratch).clone());
    if (name.includes('mirror')) mirrorBoxes.push(localBox(m, inv, scratch).clone());
    const isGlass = part === 'glass' || part === 'glassclear';
    if (part !== 'paint' && !isGlass) otherMeshes.push(m);
    if (part === 'paint') {
      if (name.includes('helmet')) {
        helmets.push(localBox(m, inv, scratch).clone());
        otherMeshes.push(m);
        return;
      }
      const box = localBox(m, inv, scratch).clone();
      box.getSize(size);
      paints.push({ box, vol: size.x * size.y * size.z });
      paintMeshes.push(m);
    } else if (isGlass && !name.includes('visor') && !name.includes('helmet') && !name.includes('lens')) {
      // (M154: a lamp lens is not a window either: the coupe's carved head
      // and tail lenses would otherwise drop the measured belt to lamp height)
      // M153: a rider's visor is not a window (since the M151 riders the bike
      // measured a "cabin" from its screen to the helmet and wore a car's collar).
      const gb = localBox(m, inv, scratch);
      glassMin = Math.min(glassMin, gb.min.y);
      glass.union(gb);
      glassMeshes.push(m);
    } else if (part === 'tyre') {
      tyres.push(localBox(m, inv, scratch).clone());
    }
  });
  const box = new THREE.Box3();
  const maxVol = paints.reduce((a, p) => Math.max(a, p.vol), 0);
  for (const p of paints) if (p.vol >= 0.15 * maxVol) box.union(p.box);
  if (box.isEmpty()) {
    for (const p of paints) box.union(p.box);
    if (box.isEmpty()) box.set(new THREE.Vector3(-1, 0, -2), new THREE.Vector3(1, 1, 2));
  }
  box.getSize(size);
  const lengthAxis: 'x' | 'z' = size.x > size.z ? 'x' : 'z';
  const widthAxis: 'x' | 'z' = lengthAxis === 'x' ? 'z' : 'x';
  const height = Math.max(size.y, 1e-6);
  const length = Math.max(size[lengthAxis], 1e-6);
  const belt =
    glassMin < Infinity
      ? Math.min(0.82, Math.max(0.35, (glassMin - box.min.y) / height))
      : DEFAULT_FIT.belt;
  const aspect = Math.min(9, Math.max(1.5, length / height));

  // M144 — the rest of the BodyFit. The fitted models put the NOSE at the
  // length axis' minimum (the M102 finding), so "metres from the nose" is
  // simply the distance from box.min along it.
  const nose = box.min[lengthAxis];
  let cabin: BodyFit['cabin'];
  let glassEnd: number | undefined;
  const bodyW = size[widthAxis];
  if (!glass.isEmpty()) {
    const x0 = Math.max(0, glass.min[lengthAxis] - nose);
    const x1 = Math.min(length, glass.max[lengthAxis] - nose);
    // A fly screen is not a cabin (the bike): it needs real length.
    if (x1 - x0 > 0.22 * length) {
      cabin = { x0, x1 };
      // M153 — the cabin ends where the SIDE windows end. The sports car's
      // engine-cover glazing (and the coupe's small fastback window) sit
      // well behind them: counted in, the collar ring ran along the rear
      // shoulder and its back rib crossed the deck just ahead of the wing.
      const sideEnd = sideGlassEnd(glassMeshes, inv, lengthAxis, widthAxis) - nose;
      if (Number.isFinite(sideEnd) && x1 - sideEnd > 0.3 && sideEnd - x0 > 0.22 * length) {
        cabin = { x0, x1: Math.min(x1, sideEnd + 0.06) };
        glassEnd = Math.max(cabin.x1, upperGlassEnd(glassMeshes, inv, lengthAxis, glass.min.y, glass.max.y) - nose);
      }
    }
    else {
      // M153 — an OPEN TOP (the muscle convertible): its only glass is a
      // short raked windscreen, so the length test failed and every kit took
      // the bike's no-cabin dress (the collar a band round the nose, the
      // cuffs round the tail, no sleeves). A car-wide windscreen with a
      // driver behind it IS a cabin: the cockpit runs from the screen's
      // base to just behind the driver's seat.
      const glassW = glass.max[widthAxis] - glass.min[widthAxis];
      const driver = helmets.find((h) => (h.min[lengthAxis] + h.max[lengthAxis]) / 2 - nose > x0);
      if (bodyW > 1.2 && glassW > 0.5 * bodyW && driver) {
        cabin = { x0, x1: Math.min(length * 0.78, driver.max[lengthAxis] - nose + 0.6) };
      }
    }
  }
  // M153 — an open COCKPIT (the Formula): no glass at all, but a car (four
  // wheels, a body wider than the bike's 0.78 m) with a driver in it. The kits ring it with the collar (it was a band
  // round the nose tip, the bike's dress) and lay the shirt's front panel
  // on the nose ahead of it.
  let cockpit: BodyFit['cockpit'];
  if (!cabin && bodyW > 0.8 && tyres.length >= 4 && helmets.length > 0) {
    const hm = helmets[0];
    const hx0 = hm.min[lengthAxis] - nose;
    const hx1 = hm.max[lengthAxis] - nose;
    if (hx0 > 0.2 * length && hx1 < 0.8 * length) cockpit = { x0: Math.max(0, hx0 - 0.55), x1: Math.min(length, hx1 + 0.3) };
  }
  // M153 — a SIDE SCOOP: a part named for one that hugs the flank (the
  // sports car's honeycomb back wall), padded out to the cavity's mouth.
  let scoop: BodyFit['scoop'];
  for (const b of scoops) {
    const flank =
      Math.abs(b.max[widthAxis] - box.max[widthAxis]) < 0.12 * bodyW ||
      Math.abs(b.min[widthAxis] - box.min[widthAxis]) < 0.12 * bodyW;
    if (!flank) continue;
    const x0 = b.min[lengthAxis] - nose - 0.12;
    const x1 = b.max[lengthAxis] - nose + 0.05;
    scoop = scoop ? { x0: Math.min(scoop.x0, x0), x1: Math.max(scoop.x1, x1) } : { x0, x1 };
  }
  // M154 polish r4: the mirrors in side-view metres, each grown FORWARD and
  // down by how far it stands out (its own width across the car): from the
  // rear three-quarter and chase views it hides that much of the door ahead
  // of and below it (behind it the door is seen).
  const mirrors: NonNullable<BodyFit['mirrors']> = [];
  for (const b of mirrorBoxes) {
    const out = Math.max(0.05, b.max[widthAxis] - b.min[widthAxis]);
    mirrors.push({
      x0: b.min[lengthAxis] - nose - out,
      x1: b.max[lengthAxis] - nose + 0.02,
      y0: box.max.y - b.max.y,
      y1: box.max.y - b.min.y + 0.9 * out,
    });
  }
  const wheels: BodyFit['wheels'] = [];
  for (const t of tyres) {
    const x = (t.min[lengthAxis] + t.max[lengthAxis]) / 2 - nose;
    const r = (t.max.y - t.min.y) / 2;
    const y = box.max.y - (t.min.y + t.max.y) / 2;
    if (r <= 0 || wheels.some((w) => Math.abs(w.x - x) < 0.2)) continue; // one per axle
    wheels.push({ x, y, r });
  }
  wheels.sort((a, b) => a.x - b.x);
  const flank = flankMap(paintMeshes, inv, box, lengthAxis, widthAxis);
  const plan = flankMap(paintMeshes, inv, box, lengthAxis, widthAxis, true, glassMeshes);
  const flankClear = flankMap(paintMeshes, inv, box, lengthAxis, widthAxis, false, otherMeshes);
  const fold = foldMap(paintMeshes, inv, box, lengthAxis, widthAxis, belt);
  const smear = foldMap(paintMeshes, inv, box, lengthAxis, widthAxis, null, true);
  const seams = flankSeams(otherMeshes, inv, box, lengthAxis, widthAxis);
  const out: LiveryMeasure = {
    box,
    lengthAxis,
    belt,
    aspect,
    L: length,
    H: height,
    W: Math.max(size[widthAxis], 1e-6),
    cabin,
    wheels,
    scoop,
    mirrors,
    flank,
    plan,
    flankClear,
    fold,
    smear,
    cockpit,
    glassEnd,
    seams,
    vehicle: vehicleId,
  };
  root.userData.liveryMeasure = out;
  return out;
}

/**
 * M153 — rasterise every OUTWARD-FACING paint triangle into a coarse grid over
 * the side view (96 x 32 cells over the painted length x height): the
 * flank map the livery painters read to keep a crest, a centre band or a
 * chest band on metal. A triangle counts when its normal points mostly
 * sideways; both flanks land in the same map. Holes stay holes: the sports
 * car's and the monster truck's side scoops, the bike's engine window, the
 * wheel arches, every cut cavity (its walls are Trim_well, not paint).
 */
/** M154 polish r5: a panel gap is a strip of non-paint at most this wide
 *  (metres along the car)... */
export const SEAM_MAX_W = 0.03;
/** ...standing at least this tall on the flank... */
export const SEAM_MIN_H = 0.15;
/** ...with (almost) none this many centimetres either side of it. */
export const SEAM_ISOLATE = 5;

/**
 * M154 polish r5 — the PANEL GAPS: side-facing non-paint triangles at the
 * flank's surface that are thin along the car (SEAM_MAX_W), summed by
 * height into 1 cm columns; a column (with its neighbours) carrying at
 * least SEAM_MIN_H of them is a gap (a door edge). Handles (thin the other
 * way), arches and flares (wide) are not. Side-view metres.
 */
export function flankSeams(meshes: THREE.Mesh[], inv: THREE.Matrix4, box: THREE.Box3, along: 'x' | 'z', across: 'x' | 'z'): { x: number; y0: number; y1: number }[] {
  const nose = box.min[along];
  const len = box.max[along] - nose;
  const bins = Math.max(1, Math.ceil(len / 0.01));
  const tall = new Float32Array(bins);
  const top = new Float32Array(bins).fill(Infinity);
  const bot = new Float32Array(bins).fill(-Infinity);
  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  const c = new THREE.Vector3();
  const e1 = new THREE.Vector3();
  const e2 = new THREE.Vector3();
  const n = new THREE.Vector3();
  const halfW = (box.max[across] - box.min[across]) / 2;
  const mid = (box.max[across] + box.min[across]) / 2;
  for (const m of meshes) {
    const pos = m.geometry.getAttribute('position');
    if (!pos) continue;
    const idx = m.geometry.index;
    const mat = new THREE.Matrix4().multiplyMatrices(inv, m.matrixWorld);
    const count = idx ? idx.count : pos.count;
    for (let i = 0; i + 2 < count; i += 3) {
      const ia = idx ? idx.getX(i) : i;
      const ib = idx ? idx.getX(i + 1) : i + 1;
      const ic = idx ? idx.getX(i + 2) : i + 2;
      a.fromBufferAttribute(pos, ia).applyMatrix4(mat);
      b.fromBufferAttribute(pos, ib).applyMatrix4(mat);
      c.fromBufferAttribute(pos, ic).applyMatrix4(mat);
      e1.subVectors(b, a);
      e2.subVectors(c, a);
      n.crossVectors(e1, e2);
      const nl = n.length();
      if (nl < 1e-9 || Math.abs(n[across]) < 0.7 * nl) continue;
      // at the flank's surface (not a part tucked inside the body)
      const out = Math.max(Math.abs(a[across] - mid), Math.abs(b[across] - mid), Math.abs(c[across] - mid));
      if (out < halfW - 0.12) continue;
      const l0 = Math.min(a[along], b[along], c[along]) - nose;
      const l1 = Math.max(a[along], b[along], c[along]) - nose;
      if (l1 - l0 > SEAM_MAX_W) continue;
      const y0 = Math.min(a.y, b.y, c.y);
      const y1 = Math.max(a.y, b.y, c.y);
      if (y1 < box.min.y || y0 > box.max.y) continue;
      const k0 = Math.max(0, Math.floor(l0 / 0.01));
      const k1 = Math.min(bins - 1, Math.floor(l1 / 0.01));
      for (let k = k0; k <= k1; k++) {
        tall[k] += (y1 - y0) / (k1 - k0 + 1);
        top[k] = Math.min(top[k]!, box.max.y - y1);
        bot[k] = Math.max(bot[k]!, box.max.y - y0);
      }
    }
  }
  // a gap stands ALONE: the panel either side of it carries none (an arch's
  // flare or a lamp is built of thin slivers too, side by side)
  const S = (k: number): number => (k < 0 || k >= bins ? 0 : (tall[k - 1] ?? 0) + tall[k]! + (tall[k + 1] ?? 0));
  const out: { x: number; y0: number; y1: number }[] = [];
  let run: { k0: number; k1: number; y0: number; y1: number } | null = null;
  const close = (): void => {
    if (run && run.y1 - run.y0 >= SEAM_MIN_H) out.push({ x: ((run.k0 + run.k1 + 1) / 2) * 0.01, y0: run.y0, y1: run.y1 });
    run = null;
  };
  for (let k = 0; k < bins; k++) {
    const sk = S(k);
    const alone = sk >= SEAM_MIN_H && S(k - SEAM_ISOLATE) < 0.3 * sk && S(k + SEAM_ISOLATE) < 0.3 * sk;
    if (!alone || tall[k]! <= 0) {
      close();
      continue;
    }
    if (run && k === run.k1 + 1) {
      run.k1 = k;
      run.y0 = Math.min(run.y0, top[k]!);
      run.y1 = Math.max(run.y1, bot[k]!);
    } else {
      close();
      run = { k0: k, k1: k, y0: top[k]!, y1: bot[k]! };
    }
  }
  close();
  return out;
}

/** M153: the furthest-back point (along the length axis, root-local) of any
 *  SIDE-facing glass triangle: the end of the side windows. -Infinity when
 *  the glass has no side windows. */
function sideGlassEnd(meshes: THREE.Mesh[], inv: THREE.Matrix4, along: 'x' | 'z', across: 'x' | 'z'): number {
  let end = -Infinity;
  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  const c = new THREE.Vector3();
  const e1 = new THREE.Vector3();
  const e2 = new THREE.Vector3();
  const nrm = new THREE.Vector3();
  for (const m of meshes) {
    const geo = m.geometry as THREE.BufferGeometry;
    const pos = geo.attributes.position;
    if (!pos) continue;
    const rel = new THREE.Matrix4().copy(inv).multiply(m.matrixWorld);
    const idx = geo.index;
    const tris = idx ? idx.count / 3 : pos.count / 3;
    for (let t = 0; t < tris; t++) {
      a.fromBufferAttribute(pos, idx ? idx.getX(t * 3) : t * 3).applyMatrix4(rel);
      b.fromBufferAttribute(pos, idx ? idx.getX(t * 3 + 1) : t * 3 + 1).applyMatrix4(rel);
      c.fromBufferAttribute(pos, idx ? idx.getX(t * 3 + 2) : t * 3 + 2).applyMatrix4(rel);
      nrm.crossVectors(e1.subVectors(b, a), e2.subVectors(c, a));
      const len = nrm.length();
      if (len < 1e-12 || Math.abs(nrm[across]) < 0.7 * len) continue;
      end = Math.max(end, a[along], b[along], c[along]);
    }
  }
  return end;
}

/** M153: the furthest-back point of the cabin's UPPER glass (triangles whose
 *  centre sits in the top half of the glass's height range): a fastback's
 *  rear window counts, low glazing over a mid engine does not. */
function upperGlassEnd(meshes: THREE.Mesh[], inv: THREE.Matrix4, along: 'x' | 'z', yMin: number, yMax: number): number {
  const mid = (yMin + yMax) / 2;
  let end = -Infinity;
  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  const c = new THREE.Vector3();
  for (const m of meshes) {
    const geo = m.geometry as THREE.BufferGeometry;
    const pos = geo.attributes.position;
    if (!pos) continue;
    const rel = new THREE.Matrix4().copy(inv).multiply(m.matrixWorld);
    const idx = geo.index;
    const tris = idx ? idx.count / 3 : pos.count / 3;
    for (let t = 0; t < tris; t++) {
      a.fromBufferAttribute(pos, idx ? idx.getX(t * 3) : t * 3).applyMatrix4(rel);
      b.fromBufferAttribute(pos, idx ? idx.getX(t * 3 + 1) : t * 3 + 1).applyMatrix4(rel);
      c.fromBufferAttribute(pos, idx ? idx.getX(t * 3 + 2) : t * 3 + 2).applyMatrix4(rel);
      if ((a.y + b.y + c.y) / 3 < mid) continue;
      end = Math.max(end, a[along], b[along], c[along]);
    }
  }
  return end;
}

function flankMap(
  meshes: THREE.Mesh[],
  inv: THREE.Matrix4,
  box: THREE.Box3,
  along: 'x' | 'z',
  across: 'x' | 'z',
  plan = false,
  occluders: THREE.Mesh[] = [],
): FlankMap {
  // plan = true: the PLAN map instead: upward-facing triangles, rows across
  // the width (from box.min across) instead of down from the roof line. It
  // keeps only the TOPMOST surface per cell: body paint under the windscreen
  // or roof glass (`occluders`) is a hole (seen from above it is glass; the
  // sports car's sash crossing landed on its windscreen).
  const cols = 96;
  const rows = 32;
  const cov = new Uint8Array(cols * rows);
  const topP = new Float32Array(cols * rows).fill(-Infinity);
  const topG = new Float32Array(cols * rows).fill(-Infinity);
  // (polish r4, plan only: how far the topmost paint in each cell faces up)
  const topN = new Float32Array(cols * rows);
  const L = Math.max(box.max[along] - box.min[along], 1e-6);
  const H = Math.max(plan ? box.max[across] - box.min[across] : box.max.y - box.min.y, 1e-6);
  const vOf = (p: THREE.Vector3): number => (plan ? ((p[across] - box.min[across]) / H) * rows : ((box.max.y - p.y) / H) * rows);
  // Depth toward the viewer: up for the plan map, out from the centre line
  // for the side map (both flanks fold onto one; the bodies are symmetric).
  const midAcross = (box.min[across] + box.max[across]) / 2;
  const depthOf = (p: THREE.Vector3): number => (plan ? p.y : Math.abs(p[across] - midAcross));
  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  const c = new THREE.Vector3();
  const e1 = new THREE.Vector3();
  const e2 = new THREE.Vector3();
  const nrm = new THREE.Vector3();
  const sources: [THREE.Mesh, boolean][] = [...meshes.map((m): [THREE.Mesh, boolean] => [m, false]), ...occluders.map((m): [THREE.Mesh, boolean] => [m, true])];
  for (const [m, isGlass] of sources) {
    const top = isGlass ? topG : topP;
    const geo = m.geometry as THREE.BufferGeometry;
    const pos = geo.attributes.position;
    if (!pos) continue;
    const rel = new THREE.Matrix4().copy(inv).multiply(m.matrixWorld);
    const idx = geo.index;
    const tris = idx ? idx.count / 3 : pos.count / 3;
    for (let t = 0; t < tris; t++) {
      const ia = idx ? idx.getX(t * 3) : t * 3;
      const ib = idx ? idx.getX(t * 3 + 1) : t * 3 + 1;
      const ic = idx ? idx.getX(t * 3 + 2) : t * 3 + 2;
      a.fromBufferAttribute(pos, ia).applyMatrix4(rel);
      b.fromBufferAttribute(pos, ib).applyMatrix4(rel);
      c.fromBufferAttribute(pos, ic).applyMatrix4(rel);
      nrm.crossVectors(e1.subVectors(b, a), e2.subVectors(c, a));
      const len = nrm.length();
      if (len < 1e-12) continue;
      // an occluder counts whichever way it faces (glass is a thin sheet; a
      // rider's leg or a tyre stands in front of the paint as a solid)
      if (isGlass ? plan && Math.abs(nrm.y) < 0.3 * len : plan ? nrm.y < 0.5 * len : Math.abs(nrm[across]) < 0.5 * len) continue;
      // cell coordinates (side view, or plan view)
      const ax = ((a[along] - box.min[along]) / L) * cols;
      const ay = vOf(a);
      const bx = ((b[along] - box.min[along]) / L) * cols;
      const by = vOf(b);
      const cx = ((c[along] - box.min[along]) / L) * cols;
      const cy = vOf(c);
      const x0 = Math.max(0, Math.floor(Math.min(ax, bx, cx)));
      const x1 = Math.min(cols - 1, Math.floor(Math.max(ax, bx, cx)));
      const y0 = Math.max(0, Math.floor(Math.min(ay, by, cy)));
      const y1 = Math.min(rows - 1, Math.floor(Math.max(ay, by, cy)));
      if (x1 < x0 || y1 < y0) continue;
      if (x1 - x0 <= 1 && y1 - y0 <= 1) {
        // a small triangle: the cell its centroid falls in
        const gx = Math.floor((ax + bx + cx) / 3);
        const gy = Math.floor((ay + by + cy) / 3);
        if (gx >= 0 && gx < cols && gy >= 0 && gy < rows) {
          const k = gy * cols + gx;
          const d = (depthOf(a) + depthOf(b) + depthOf(c)) / 3;
          if (d > top[k]) {
            top[k] = d;
            if (!isGlass) topN[k] = nrm.y / len;
          }
        }
        continue;
      }
      const area = (bx - ax) * (cy - ay) - (by - ay) * (cx - ax);
      if (Math.abs(area) < 1e-9) continue;
      for (let gy = y0; gy <= y1; gy++) {
        for (let gx = x0; gx <= x1; gx++) {
          const px = gx + 0.5;
          const py = gy + 0.5;
          const w0 = ((bx - px) * (cy - py) - (by - py) * (cx - px)) / area;
          const w1 = ((cx - px) * (ay - py) - (cy - py) * (ax - px)) / area;
          const w2 = 1 - w0 - w1;
          if (w0 >= -0.02 && w1 >= -0.02 && w2 >= -0.02) {
            const k = gy * cols + gx;
            const d = w0 * depthOf(a) + w1 * depthOf(b) + w2 * depthOf(c);
            if (d > top[k]) {
              top[k] = d;
              if (!isGlass) topN[k] = nrm.y / len;
            }
          }
        }
      }
    }
  }
  const eps = plan ? 0.004 : 0.012;
  for (let k = 0; k < cols * rows; k++) cov[k] = topP[k] > -Infinity && topP[k] > topG[k] - eps ? 1 : 0;
  // Close pinholes (a cell with paint on both sides along a row or column).
  const closed = cov.slice();
  for (let r = 0; r < rows; r++) {
    for (let col = 0; col < cols; col++) {
      if (cov[r * cols + col]) continue;
      const lr = col > 0 && col < cols - 1 && cov[r * cols + col - 1] && cov[r * cols + col + 1];
      const ud = r > 0 && r < rows - 1 && cov[(r - 1) * cols + col] && cov[(r + 1) * cols + col];
      if (lr || ud) closed[r * cols + col] = 1;
    }
  }
  if (!plan) return { cols, rows, cov: closed, depth: topP };
  // (polish r4) the plan map also keeps the topmost paint's height (metres
  // down from the roof line, as the side painting counts it) and how far it
  // faces up: where a kit's shoulder is (kits.drapeShoulders)
  const height = new Float32Array(cols * rows).fill(-1);
  for (let k = 0; k < cols * rows; k++) if (topP[k] > -Infinity) height[k] = box.max.y - topP[k];
  return { cols, rows, cov: closed, height, ny: topN };
}

/** M154 polish: a paint surface this steep or flatter (the normal's up
 *  component, of 1) is a SHOULDER for the fold map; anything facing up at
 *  all (FOLD_PLAN_NY) counts toward what hides a lower one from above. */
const FOLD_NY = 0.7;
const FOLD_PLAN_NY = 0.3;
/** M154 polish: an up-facing surface this wide (in from the flank) is a
 *  DECK a crest folds over (the Formula's sidepod tops are 30 cm). */
const FOLD_SPAN = 0.1;
/** M154 polish r2: a surface facing sideways less than this (its normal's
 *  across component, of 1) takes the side painting smeared across it. */
const SMEAR_NX = 0.45;

/**
 * M154 polish — the FOLD MAP: the side-view cells (the flank map's grid) over
 * which a paint surface that faces mostly UP, and is the top of the body
 * there, lies. The side-view flag painting lands on such a surface stretched
 * along it, so a crest painted there runs over the shoulder (the Formula's
 * sidepod tops took the top of Japan's disc and most of Brazil's diamond, and
 * the USA's stars smeared into streaks on its low nose). Sampled over every
 * up-facing paint triangle; both flanks fold onto one grid, as in the flank
 * map. A surface hidden under another (a floor under a sidepod) is skipped.
 */
function foldMap(
  meshes: THREE.Mesh[],
  inv: THREE.Matrix4,
  box: THREE.Box3,
  along: 'x' | 'z',
  across: 'x' | 'z',
  belt: number | null,
  /** M154 polish r2: the SMEAR map instead (belt null): every broad top
   *  surface whose normal leans up at all and not sideways, at any height —
   *  a steep hood plane, a nose, a deck — over which the side painting is
   *  drawn across the width (the USA's stars tore into white slashes over
   *  the monster truck's and the sports car's hoods). */
  smear = false,
): FlankMap {
  const cols = 96;
  const rows = 32;
  const cov = new Uint8Array(cols * rows);
  const L = Math.max(box.max[along] - box.min[along], 1e-6);
  const H = Math.max(box.max.y - box.min.y, 1e-6);
  const W = Math.max(box.max[across] - box.min[across], 1e-6);
  // the top of the up-facing paint seen from above, on a coarse plan grid
  const top = new Float32Array(cols * rows).fill(-Infinity);
  const planCell = (p: THREE.Vector3): number => {
    const i = Math.floor(((p[along] - box.min[along]) / L) * cols);
    const j = Math.floor(((p[across] - box.min[across]) / W) * rows);
    return i >= 0 && i < cols && j >= 0 && j < rows ? j * cols + i : -1;
  };
  const step = Math.min(L / cols, H / rows, W / rows) / 1.5;
  const tris: { a: THREE.Vector3; b: THREE.Vector3; c: THREE.Vector3; ny: number }[] = [];
  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  const c = new THREE.Vector3();
  const e1 = new THREE.Vector3();
  const e2 = new THREE.Vector3();
  const nrm = new THREE.Vector3();
  for (const m of meshes) {
    const geo = m.geometry as THREE.BufferGeometry;
    const pos = geo.attributes.position;
    if (!pos) continue;
    const rel = new THREE.Matrix4().copy(inv).multiply(m.matrixWorld);
    const idx = geo.index;
    const n = idx ? idx.count / 3 : pos.count / 3;
    for (let t = 0; t < n; t++) {
      a.fromBufferAttribute(pos, idx ? idx.getX(t * 3) : t * 3).applyMatrix4(rel);
      b.fromBufferAttribute(pos, idx ? idx.getX(t * 3 + 1) : t * 3 + 1).applyMatrix4(rel);
      c.fromBufferAttribute(pos, idx ? idx.getX(t * 3 + 2) : t * 3 + 2).applyMatrix4(rel);
      nrm.crossVectors(e1.subVectors(b, a), e2.subVectors(c, a));
      const len = nrm.length();
      if (len < 1e-12 || nrm.y < FOLD_PLAN_NY * len) continue;
      // (the smear map's span counts a surface facing up-ish and NOT
      // sideways: a door's rounded top edge faces out and paints fine)
      const ny = nrm.y / len;
      tris.push({ a: a.clone(), b: b.clone(), c: c.clone(), ny: smear ? (Math.abs(nrm[across]) <= SMEAR_NX * len ? 1 : 0) : ny });
    }
  }
  const p = new THREE.Vector3();
  const each = (t: { a: THREE.Vector3; b: THREE.Vector3; c: THREE.Vector3 }, f: (q: THREE.Vector3) => void): void => {
    const span = Math.max(t.a.distanceTo(t.b), t.a.distanceTo(t.c), t.b.distanceTo(t.c));
    const k = Math.max(1, Math.ceil(span / step));
    for (let i = 0; i <= k; i++) {
      for (let j = 0; j <= k - i; j++) {
        const u = i / k;
        const v = j / k;
        p.copy(t.a).multiplyScalar(1 - u - v).addScaledVector(t.b, u).addScaledVector(t.c, v);
        f(p);
      }
    }
  };
  for (const t of tris) {
    each(t, (q) => {
      const k = planCell(q);
      if (k >= 0 && q.y > top[k]!) top[k] = q.y;
    });
  }
  // how far in from the flank (both flanks folded onto one) the up-facing
  // paint reaches in each side cell: a crest only folds over a BROAD deck
  // (a sidepod top, a low nose); the rounded top edge of a door or a
  // fairing's lip turns away within a few centimetres and costs nothing
  const mid = (box.min[across] + box.max[across]) / 2;
  const lo = new Float32Array(cols * rows).fill(Infinity);
  const hi = new Float32Array(cols * rows).fill(-Infinity);
  for (const t of tris) {
    if (t.ny < FOLD_NY) continue;
    each(t, (q) => {
      const k = planCell(q);
      if (k < 0 || q.y < top[k]! - 0.02) return;
      const i = Math.floor(((q[along] - box.min[along]) / L) * cols);
      const j = Math.floor(((box.max.y - q.y) / H) * rows);
      if (i < 0 || i >= cols || j < 0 || j >= rows) return;
      const d = Math.abs(q[across] - mid);
      const c = j * cols + i;
      if (d < lo[c]!) lo[c] = d;
      if (d > hi[c]!) hi[c] = d;
    });
  }
  // (only UNDER the belt line: at it, every car's door crest meets its
  // shoulder and hood, which the crests have always been fitted to; the
  // smear map at every height)
  const r0 = belt === null ? 0 : Math.ceil((1 - belt) * rows) + 1;
  for (let c = r0 * cols; c < cols * rows; c++) if (hi[c]! - lo[c]! >= FOLD_SPAN) cov[c] = 1;
  // ...and the row under a deck's edge: the shoulder rounding down from it
  // to the wall turns away from every view but the side one, so a crest
  // reaching up into it reads cut off along it (the Formula's sun)
  for (let r = rows - 1; r > r0; r--) for (let i = 0; i < cols; i++) if (cov[(r - 1) * cols + i] === 1 && hi[(r - 1) * cols + i]! - lo[(r - 1) * cols + i]! >= FOLD_SPAN) cov[r * cols + i] = 1;
  return { cols, rows, cov };
}

/**
 * Wrap UV projection: the livery canvas is a SIDE-VIEW painting, so the UVs
 * are the matching side-view projection over the MEASURED painted body —
 * u = along the car (nose 0 → tail 1), v = pure height (sill 0 → roof 1),
 * both clamped so bolt-ons outside the body sample the livery's edge. Both
 * flanks sample identical coordinates, so each side shows the complete art.
 * Written once per geometry (deterministic, so shared clones agree) —
 * overwrites the GLB's loft UVs, which nothing else reads.
 */
export function projectWrapUVs(root: THREE.Object3D, mesh: THREE.Mesh, m: LiveryMeasure): void {
  const geo = mesh.geometry as THREE.BufferGeometry;
  if (!geo.attributes.position || geo.userData.wrapUv) return;
  root.updateMatrixWorld(true);
  const rel = root.matrixWorld.clone().invert().multiply(mesh.matrixWorld);
  const size = new THREE.Vector3();
  m.box.getSize(size);
  const len = Math.max(size[m.lengthAxis], 1e-6);
  const height = Math.max(size.y, 1e-6);
  const min = m.box.min;
  const pos = geo.attributes.position;
  const uv = new Float32Array(pos.count * 2);
  const p = new THREE.Vector3();
  for (let i = 0; i < pos.count; i++) {
    p.fromBufferAttribute(pos, i).applyMatrix4(rel);
    uv[i * 2] = clamp01((p[m.lengthAxis] - min[m.lengthAxis]) / len);
    uv[i * 2 + 1] = clamp01((p.y - min.y) / height);
  }
  geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  geo.userData.wrapUv = true;
}

/**
 * M150 — box-projected UVs for the woven CARBON aero parts. The Blender
 * builds emit no UV layer on bolt-ons; a per-vertex projection by the
 * dominant normal axis (the classic triplanar pick, resolved per vertex)
 * tiles the weave at `scale` cells per metre on every face. Deterministic
 * and written once per geometry, like the wrap projection.
 */
export function projectBoxUVs(mesh: THREE.Mesh, scale = 40): void {
  const geo = mesh.geometry as THREE.BufferGeometry;
  if (!geo.attributes.position || geo.userData.boxUv) return;
  if (!geo.attributes.normal) geo.computeVertexNormals();
  const pos = geo.attributes.position;
  const nrm = geo.attributes.normal;
  const uv = new Float32Array(pos.count * 2);
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const y = pos.getY(i);
    const z = pos.getZ(i);
    const ax = Math.abs(nrm.getX(i));
    const ay = Math.abs(nrm.getY(i));
    const az = Math.abs(nrm.getZ(i));
    let u: number;
    let v: number;
    if (ax >= ay && ax >= az) {
      u = y;
      v = z;
    } else if (ay >= az) {
      u = x;
      v = z;
    } else {
      u = x;
      v = y;
    }
    uv[i * 2] = u * scale;
    uv[i * 2 + 1] = v * scale;
  }
  geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  geo.userData.boxUv = true;
}

let flakeTex: THREE.DataTexture | null = null;

/** M152 — the metallic FLAKE under the clearcoat (the owner's "texture of
 *  paint" note): one shared 64x64 normal map of randomly tilted facets, tiled
 *  fine over the body through box-projected UVs, so the base coat sparkles
 *  under the IBL while the clearcoat on top stays glass-smooth. Deterministic
 *  (a fixed-seed generator) so every ride and every reload sparkle alike. */
export function paintFlakeTexture(): THREE.DataTexture {
  if (flakeTex) return flakeTex;
  const size = 64;
  const data = new Uint8Array(size * size * 4);
  let seed = 0x9e3779b9;
  const rnd = (): number => {
    // mulberry32 — the same tiny generator the game uses elsewhere
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  for (let i = 0; i < size * size; i++) {
    // a facet: a random tilt of up to ~0.42 off the surface normal
    const a = rnd() * Math.PI * 2;
    const tilt = Math.sqrt(rnd()) * 0.42;
    const nx = Math.cos(a) * tilt;
    const ny = Math.sin(a) * tilt;
    const nz = Math.sqrt(Math.max(0, 1 - nx * nx - ny * ny));
    data[i * 4] = Math.round((nx * 0.5 + 0.5) * 255);
    data[i * 4 + 1] = Math.round((ny * 0.5 + 0.5) * 255);
    data[i * 4 + 2] = Math.round((nz * 0.5 + 0.5) * 255);
    data[i * 4 + 3] = 255;
  }
  flakeTex = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  flakeTex.wrapS = THREE.RepeatWrapping;
  flakeTex.wrapT = THREE.RepeatWrapping;
  flakeTex.magFilter = THREE.LinearFilter;
  flakeTex.minFilter = THREE.LinearMipmapLinearFilter;
  flakeTex.generateMipmaps = true;
  flakeTex.needsUpdate = true;
  return flakeTex;
}

/** M152 — give a paint mesh UVs for the flake map when it shipped none (the
 *  lofted shells carry no UV layer). Box-projected at 1 unit per metre so the
 *  flake texture's own `repeat` sets the flake size; marked separately from
 *  the wrap/box flags so a later wrap still gets its draped projection. */
export function ensureFlakeUVs(mesh: THREE.Mesh): void {
  const geo = mesh.geometry as THREE.BufferGeometry;
  if (!geo.attributes.position || geo.attributes.uv || geo.userData.flakeUv) return;
  projectBoxUVs(mesh, 1);
  delete geo.userData.boxUv;
  geo.userData.flakeUv = true;
}

let carbonTex: THREE.Texture | null = null;

/** The 2x2 twill carbon-fibre weave (one shared, repeating canvas texture):
 *  dark tows with a soft sheen gradient per tile so the clearcoat reflection
 *  does the talking. Values stay dark (the cloth is near black) — the part
 *  reads as carbon through the reflection pattern, not through contrast. */
export function carbonWeaveTexture(): THREE.Texture {
  if (carbonTex) return carbonTex;
  const size = 128;
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const g = c.getContext('2d')!;
  const cells = 8;
  const cs = size / cells;
  for (let j = 0; j < cells; j++) {
    for (let i = 0; i < cells; i++) {
      const horiz = (i + j) % 2 === 0;
      const x = i * cs;
      const y = j * cs;
      const grad = horiz ? g.createLinearGradient(x, y, x, y + cs) : g.createLinearGradient(x, y, x + cs, y);
      // M153 (the owner's briefs: a BLACK splitter, wing, diffuser and
      // floor): near-black tows with a soft sheen. The old mid-grey weave
      // under the clearcoat read as slate-grey plastic on every ride.
      grad.addColorStop(0, '#0d0e11');
      grad.addColorStop(0.45, '#1d1f24');
      grad.addColorStop(0.55, '#1a1c20');
      grad.addColorStop(1, '#0a0b0d');
      g.fillStyle = grad;
      g.fillRect(x, y, cs, cs);
      // the tow's edge shadow
      g.fillStyle = 'rgba(0,0,0,0.35)';
      if (horiz) g.fillRect(x, y + cs - 1, cs, 1);
      else g.fillRect(x + cs - 1, y, 1, cs);
    }
  }
  carbonTex = new THREE.CanvasTexture(c);
  carbonTex.colorSpace = THREE.SRGBColorSpace;
  carbonTex.wrapS = THREE.RepeatWrapping;
  carbonTex.wrapT = THREE.RepeatWrapping;
  carbonTex.anisotropy = 4;
  return carbonTex;
}

/**
 * M144 — the THREE-VIEW projection attributes (livery/atlas.ts reads them):
 * `lvPos` = where the vertex sits inside the measured body box as
 * (across 0..1, up 0..1, along 0..1 with the nose at 0), and `lvNrm` = which
 * way its surface faces in the same axes. The paint shader turns the pair
 * into "this pixel is flank / hood / nose" per PIXEL. Written once per
 * geometry (deterministic, so shared clones agree), beside the side-view UVs
 * the flag wraps keep using.
 */
export function projectLiveryViews(root: THREE.Object3D, mesh: THREE.Mesh, m: LiveryMeasure): void {
  const geo = mesh.geometry as THREE.BufferGeometry;
  if (!geo.attributes.position || geo.userData.lvViews) return;
  if (!geo.attributes.normal) geo.computeVertexNormals();
  root.updateMatrixWorld(true);
  const rel = root.matrixWorld.clone().invert().multiply(mesh.matrixWorld);
  const nrmMat = new THREE.Matrix3().getNormalMatrix(rel);
  const size = new THREE.Vector3();
  m.box.getSize(size);
  const along = m.lengthAxis;
  const across: 'x' | 'z' = along === 'x' ? 'z' : 'x';
  const min = m.box.min;
  const pos = geo.attributes.position;
  const nrm = geo.attributes.normal;
  const outP = new Float32Array(pos.count * 3);
  const outN = new Float32Array(pos.count * 3);
  const p = new THREE.Vector3();
  const n = new THREE.Vector3();
  const smooth = smoothedViewNormals(geo, rel);
  // M153: a MIRROR CAP faces forward, so by its normal it read the FRONT
  // view's top edge (the dragon's fire: a red cap on a green door, measured
  // on the coupe + sports sheets). A cap is body-colour on a wrapped car: it
  // reads the flank painting at its own spot, like the door it hangs beside.
  const capOnFlank = /mirror/i.test(mesh.name);
  for (let i = 0; i < pos.count; i++) {
    p.fromBufferAttribute(pos, i).applyMatrix4(rel);
    n.fromBufferAttribute(nrm, i).applyMatrix3(nrmMat).normalize();
    outP[i * 3] = (p[across] - min[across]) / Math.max(size[across], 1e-6);
    outP[i * 3 + 1] = (p.y - min.y) / Math.max(size.y, 1e-6);
    outP[i * 3 + 2] = (p[along] - min[along]) / Math.max(size[along], 1e-6);
    if (capOnFlank) {
      outN[i * 3] = outP[i * 3] < 0.5 ? -1 : 1;
      outN[i * 3 + 1] = 0;
      outN[i * 3 + 2] = 0;
    } else {
      // M153: the view is chosen from a SMOOTHED normal (see
      // smoothedViewNormals), so the change from flank to hood follows one
      // clean line instead of flipping facet by facet.
      n.set(smooth[i * 3], smooth[i * 3 + 1], smooth[i * 3 + 2]);
      outN[i * 3] = n[across];
      outN[i * 3 + 1] = n.y;
      outN[i * 3 + 2] = n[along];
    }
  }
  geo.setAttribute('lvPos', new THREE.BufferAttribute(outP, 3));
  geo.setAttribute('lvNrm', new THREE.BufferAttribute(outN, 3));
  geo.userData.lvViews = true;
}

/**
 * M153 — the normal the three-view livery CHOOSES ITS VIEW by, per vertex, in
 * root space: the mesh welded by position (so creases and split normals are
 * one surface), area-weighted face normals averaged per welded vertex, then
 * four rounds of neighbour averaging. The rebuilt fleet's panels are bevelled,
 * faceted and boolean-cut; with the shading normals, neighbouring facets on a
 * shoulder flipped between "flank" and "hood" and a striped kit tore into
 * shards along every haunch (the sports car's, measured on the lab sheets).
 * Lighting still uses the real normals; this one only picks the painting.
 */
function smoothedViewNormals(geo: THREE.BufferGeometry, rel: THREE.Matrix4): Float32Array {
  const pos = geo.attributes.position;
  const count = pos.count;
  const weld = new Int32Array(count);
  const keys = new Map<string, number>();
  const pts: number[] = [];
  const v = new THREE.Vector3();
  for (let i = 0; i < count; i++) {
    v.fromBufferAttribute(pos, i).applyMatrix4(rel);
    const key = `${Math.round(v.x * 500)},${Math.round(v.y * 500)},${Math.round(v.z * 500)}`;
    let w = keys.get(key);
    if (w === undefined) {
      w = pts.length / 3;
      keys.set(key, w);
      pts.push(v.x, v.y, v.z);
    }
    weld[i] = w;
  }
  const nw = pts.length / 3;
  const idx = geo.index;
  const tris = idx ? idx.count / 3 : count / 3;
  const corner = (t: number, k: number): number => weld[idx ? idx.getX(t * 3 + k) : t * 3 + k];
  let acc = new Float32Array(nw * 3);
  for (let t = 0; t < tris; t++) {
    const a = corner(t, 0);
    const b = corner(t, 1);
    const c = corner(t, 2);
    const ux = pts[b * 3] - pts[a * 3];
    const uy = pts[b * 3 + 1] - pts[a * 3 + 1];
    const uz = pts[b * 3 + 2] - pts[a * 3 + 2];
    const wx = pts[c * 3] - pts[a * 3];
    const wy = pts[c * 3 + 1] - pts[a * 3 + 1];
    const wz = pts[c * 3 + 2] - pts[a * 3 + 2];
    const nx = uy * wz - uz * wy;
    const ny = uz * wx - ux * wz;
    const nz = ux * wy - uy * wx;
    for (const q of [a, b, c]) {
      acc[q * 3] += nx;
      acc[q * 3 + 1] += ny;
      acc[q * 3 + 2] += nz;
    }
  }
  const unit = (arr: Float32Array): void => {
    for (let q = 0; q < nw; q++) {
      const l = Math.hypot(arr[q * 3], arr[q * 3 + 1], arr[q * 3 + 2]) || 1;
      arr[q * 3] /= l;
      arr[q * 3 + 1] /= l;
      arr[q * 3 + 2] /= l;
    }
  };
  unit(acc);
  for (let round = 0; round < 4; round++) {
    const next = new Float32Array(nw * 3);
    for (let t = 0; t < tris; t++) {
      const a = corner(t, 0);
      const b = corner(t, 1);
      const c = corner(t, 2);
      const sx = acc[a * 3] + acc[b * 3] + acc[c * 3];
      const sy = acc[a * 3 + 1] + acc[b * 3 + 1] + acc[c * 3 + 1];
      const sz = acc[a * 3 + 2] + acc[b * 3 + 2] + acc[c * 3 + 2];
      for (const q of [a, b, c]) {
        next[q * 3] += sx;
        next[q * 3 + 1] += sy;
        next[q * 3 + 2] += sz;
      }
    }
    unit(next);
    acc = next;
  }
  const out = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    out[i * 3] = acc[weld[i] * 3];
    out[i * 3 + 1] = acc[weld[i] * 3 + 1];
    out[i * 3 + 2] = acc[weld[i] * 3 + 2];
  }
  return out;
}

// ---------------------------------------------------------------------------
// Livery textures — one per (item, fit). Every car of the same vehicle wearing
// the same item shares one texture; the generic DEFAULT_FIT texture serves
// the procedural placeholder and the dozer form.
// ---------------------------------------------------------------------------

const liveryCache = new Map<string, THREE.Texture>();
/** Most-recently-used keys last. A shop browse paints one texture per card it
 *  previews; without a cap an evening in the shop would hold every one of
 *  them on the GPU. Evicted textures are disposed — a car still wearing one
 *  simply re-uploads it on its next draw (the canvas lives with the texture). */
const LIVERY_CACHE_MAX = 14;

function remember(key: string, tex: THREE.Texture): THREE.Texture {
  liveryCache.delete(key);
  liveryCache.set(key, tex);
  while (liveryCache.size > LIVERY_CACHE_MAX) {
    const oldest = liveryCache.keys().next().value as string;
    const old = liveryCache.get(oldest)!;
    liveryCache.delete(oldest);
    (old.userData.lvData as THREE.Texture | undefined)?.dispose();
    old.dispose();
  }
  return tex;
}

/** QA (the livery lab): paint a labelled test card instead of the kit. */
let liveryDebug = false;
export function setLiveryDebug(on: boolean): void {
  liveryDebug = on;
  liveryCache.clear();
}

const isBody = (fit: LiveryFit | BodyFit): fit is BodyFit => 'L' in fit;

/** The body texture for `livery` on one measured body. Flag wraps are a
 *  side-view painting (M127); kit paint jobs are a THREE-VIEW atlas (M144),
 *  which the paint material reads through livery/atlas.patchLiveryMaterial —
 *  `tex.userData.lv3` tells the two apart. */
export function liveryTexture(livery: LiverySpec, fit: LiveryFit | LiveryMeasure = DEFAULT_FIT): THREE.Texture {
  const body = isBody(fit) ? fit : DEFAULT_BODY;
  const key = livery.kit
    ? `${livery.id}|${body.vehicle}|${body.L.toFixed(2)}|${body.H.toFixed(2)}|${body.W.toFixed(2)}`
    : `${livery.id}|${fit.belt.toFixed(2)}|${fit.aspect.toFixed(1)}|${isBody(fit) ? `${fit.vehicle}|${fit.L.toFixed(2)}` : ''}`;
  const hit = liveryCache.get(key);
  if (hit) return remember(key, hit);
  let tex: THREE.Texture;
  if (livery.kit) {
    const kit = kitLivery(livery.kit);
    tex = liveryDebug
      ? buildLiveryAtlas(debugPainter, body).map
      : buildLiveryAtlas(kit.paint, body, { data: kit.data, res: kit.res }).map;
    tex.userData.lvMirror = kit.mirror && !liveryDebug;
    tex.userData.lvBump = kit.bump;
    tex.userData.lvGlow = kit.glow;
    tex.userData.lvClearcoat = kit.clearcoat;
  } else {
    const extras = newWrapExtras();
    tex = new THREE.CanvasTexture(wrapCanvas(livery.flag!, fit, 1024, 512, extras));
    // M154 polish r5: the Formula's crests and canton stars laid whole on
    // its tops (wrapTopDecal), read by the paint material's decal patch
    const decal = isBody(fit) && 'box' in fit ? wrapTopDecal(fit as LiveryMeasure, extras) : null;
    if (decal) {
      const d = new THREE.CanvasTexture(decal.canvas);
      d.colorSpace = THREE.SRGBColorSpace;
      d.wrapS = d.wrapT = THREE.ClampToEdgeWrapping;
      d.anisotropy = 8;
      tex.userData.lvDecal = d;
      tex.userData.lvDecalMinY = decal.minY;
    }
    tex.colorSpace = THREE.SRGBColorSpace;
    // The projection is clamped to [0,1] — clamp the sampler too, so a float
    // overshoot at the nose can never wrap the tail colour onto it.
    tex.wrapS = THREE.ClampToEdgeWrapping;
    tex.wrapT = THREE.ClampToEdgeWrapping;
    tex.anisotropy = 4; // crisp stripes at the glancing angles a chase cam sees
  }
  return remember(key, tex);
}

/**
 * The livery as ONE flat side-view painting for the generic body: what the
 * procedural placeholder and the bulldozer form wear (plain UVs, no views).
 * Kits only — a flag's generic texture is already a side view.
 */
function flatLiveryTexture(livery: LiverySpec): THREE.Texture {
  const key = `${livery.id}|flat`;
  const hit = liveryCache.get(key);
  if (hit) return remember(key, hit);
  const tex = new THREE.CanvasTexture(buildLiverySide(kitLivery(livery.kit!).paint, DEFAULT_BODY, 512));
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  return remember(key, tex);
}

/** Resolve a saved loadout into concrete material inputs. Undefined fields =
 *  stock. A wrap's map wins over a kit's; tires own the rim colour when both
 *  a kit accent and a tire set apply. */
export function resolveLook(loadout: VehicleLoadout): CarLook | undefined {
  const look: CarLook = {};
  const kit = loadout.paint ? cosmeticById(loadout.paint)?.paint : undefined;
  if (kit && loadout.paint) {
    look.body = kit.colors[0];
    look.trim = kit.trim ?? kit.colors[1];
    if (kit.accent !== undefined) look.rim = kit.accent;
    look.livery = { id: loadout.paint, kit };
    look.wrapMap = flatLiveryTexture(look.livery);
  }
  const flag = loadout.wrap ? cosmeticById(loadout.wrap)?.flag : undefined;
  if (flag && loadout.wrap) {
    // M102: wraps paint the side-view LIVERY layout (wrapCanvas), not the
    // raw flag — the shop cards keep the flag-shaped art (flagCanvas).
    look.livery = { id: loadout.wrap, flag };
    look.wrapMap = liveryTexture(look.livery);
  }
  const tires = loadout.tires ? cosmeticById(loadout.tires)?.tires : undefined;
  if (tires) {
    look.tyre = tires.tyre;
    look.rim = tires.rim;
    look.rimChrome = tires.chrome ?? false;
    look.tyreMetal = tires.metal ?? false;
    if (tires.art) look.wheelArt = tires.art;
  }
  // M128: mods stack — keep the real mod ids only (the parts check the ride).
  const mods = (loadout.mods ?? []).filter((id) => cosmeticById(id)?.category === 'mod');
  if (mods.length > 0) {
    look.mods = {
      ids: mods,
      kitNumber: isKitNumber(loadout.kitNumber) ? loadout.kitNumber : KIT_NUMBER_DEFAULT,
      tifoStyle: isTifoStyle(loadout.tifoStyle) ? loadout.tifoStyle : TIFO_STYLE_DEFAULT,
    };
  }
  return Object.keys(look).length > 0 ? look : undefined;
}

// ---------------------------------------------------------------------------
// M114b — PER-VEHICLE STOCK PAINT (owner: "make the body of the Formula car a
// chrome to blue gradient instead of the gold yellow").
//
// WHY THIS LIVES IN CODE, NOT IN THE GLB: `applyCarMaterials` BUILDS a fresh
// MeshPhysicalMaterial for every Paint-named material and assigns it, so any
// gradient baked into the model's own Paint material is discarded in game (it
// only survives in the Blender-rendered shop card). The gradient therefore
// rides the M102 wrap pipeline: a canvas whose X axis runs ALONG the car,
// sampled through `projectWrapUVs` (u = length, v = height) — exactly the
// axis a nose→tail gradient needs.
//
// It is the STOCK treatment: any equipped body cosmetic (a flag wrap or a kit
// paint job — the M87 merged body slot) fully overrides it, same as it
// overrides every other vehicle's flat stock livery.
// ---------------------------------------------------------------------------

/** The render inputs a vehicle's stock paint treatment adds on top of the
 *  normal clearcoat body material (M114b — only the Formula has one today). */
export interface StockPaint {
  map: THREE.Texture;
  metalness: number;
  roughness: number;
  envMapIntensity: number;
}

/**
 * The stock paint treatment for `vehicleId`, or undefined for a plain flat
 * livery. M151: NO ride carries one any more — the owner's approved concept
 * pack paints the Formula the same solid red as the other red rides, so the
 * M114b chrome→blue gradient (formulaPaintCanvas) is retired. The hook stays
 * for a future per-vehicle treatment; an equipped BODY cosmetic (wrap or kit
 * paint job) would beat it by construction — the shop always wins the body
 * slot (M87).
 */
export function stockPaintFor(vehicleId: string, look?: CarLook): StockPaint | undefined {
  void vehicleId;
  void look;
  return undefined;
}

// ---------------------------------------------------------------------------
// The equipped PLAYER look (offline races). Flow publishes; Race reads.
//
// M87: the look travels WITH A KEY (the serialized loadout string) so the
// offline race can rebuild the player's car view when the key changes —
// mirroring NetRace's M81 look-key rebuild. Without it the view captured the
// look ONCE at construction, and the attract world is built BEFORE the save
// loads: a player whose ride matched the seam default never saw any cosmetic
// in a race (the owner's "my paint job doesn't show" bug), and everyone else
// raced whatever look was equipped at the LAST vehicle change.
// ---------------------------------------------------------------------------

let playerLook: CarLook | undefined;
let playerLookKey = '';
/** M109: WHICH ride this look belongs to. The seam is per-vehicle (loadouts
 *  are), so anything reading it for a specific car must check the ride matches
 *  — see Game.netLookFor, where it backstops the online look for our OWN seat. */
let playerLookVehicle = '';

export function setPlayerLook(look: CarLook | undefined, key = '', vehicleId = ''): void {
  playerLook = look;
  playerLookKey = key;
  playerLookVehicle = vehicleId;
}

export function getPlayerLook(): CarLook | undefined {
  return playerLook;
}

/** The serialized-loadout key for the current player look ('' = stock). */
export function getPlayerLookKey(): string {
  return playerLookKey;
}

/** The vehicle id the current player look was published for ('' = unknown). */
export function getPlayerLookVehicle(): string {
  return playerLookVehicle;
}
